import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { formatDate, formatTime } from './utils/helpers';
import { checkAuthAndRedirect, isAdmin } from './utils/auth';
import { format } from 'date-fns';
import './scss/Dashboard.scss';
import { FaShareAlt, FaUser, FaUserCircle, FaTrophy, FaUsers, FaUserPlus, FaRunning, FaCrown, FaChevronRight, FaUserFriends, FaArrowLeft, FaClock, FaCheck } from 'react-icons/fa';
import { MdSportsSoccer } from 'react-icons/md';
import SportsIcon from '@mui/icons-material/Sports';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import Marquee from './Marquee';
import Awards from './components/Awards';
import AchievementModal from './components/AchievementModal';
import { achievements } from './data/achievements';
import MatchCard from './components/match/MatchCard';
import TrainingList from './components/training/TrainingList';
import CalendarCarousel from './components/calendar/CalendarCarousel';
import api from './services/apiService';
import { authService } from './services/authService';
import ProFeatureModal from './components/ProFeatureModal';
import confetti from 'canvas-confetti';
import { useNotification } from './components/NotificationProvider';
import WelcomeModal from './components/modals/WelcomeModal';
import ParentArticles from './components/parent/ParentArticles';
import MatchReports from './components/MatchReports';
import logo from './images/logo2.svg';
import SeasonWrappedModal from './components/SeasonWrapped/SeasonWrappedModal';
import SeasonWrapped from './components/SeasonWrapped/SeasonWrapped';
import PushPermissionBanner from './components/PushPermissionBanner';
import { notifyAvailabilityResponseSaved } from './services/pushPermissionReminders';
import { isYouthTeam } from './utils/playerUtils';

// Set to true when re-enabling the parent New Year Pro offer banner
const SHOW_NEW_YEAR_PARENT_OFFER = false;

function Dashboard() {
  // Add a forceUpdate function
  const [, forceUpdate] = useState({});
  const rerender = () => forceUpdate({});
  
  const [nextGameDate, setNextGameDate] = useState('');
  const [response, setResponse] = useState('Invited');
  const [location, setLocation] = useState('');
  const [firstName, setFirstName] = useState('Player');
  const [counts, setCounts] = useState({ invited: 0, yes: 0, no: 0 });
  const [matchId, setMatchId] = useState(null);
  const [matchTime, setMatchTime] = useState('');
  const [matchResults, setMatchResults] = useState([]);
  const [matches, setMatches] = useState([]);
  const navigate = useNavigate();
  const [nextMatch, setNextMatch] = useState(null);
  const [player, setPlayer] = useState(null);
  const [showAchievement, setShowAchievement] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [futureMatches, setFutureMatches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  const [teamId, setTeamId] = useState(null);
  const [recentMatches, setRecentMatches] = useState([]);
  const [lastResults, setLastResults] = useState([]);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [currentSeasonId, setCurrentSeasonId] = useState(null);
  const [seasonLoaded, setSeasonLoaded] = useState(false);
  const [hasPreviousSeasons, setHasPreviousSeasons] = useState(false);
  
  // Add a ref to track if training data has been loaded
  const trainingDataFetchedRef = useRef(false);
  // Add a ref to track if season data has been fetched to prevent multiple calls
  const seasonDataFetchedRef = useRef(false);
  
  // Add the getCachedTrainingSessions function here
  const getCachedTrainingSessions = () => {
    try {
      const cached = sessionStorage.getItem('training_sessions');
      if (cached) {
        const sessions = JSON.parse(cached);
        if (Array.isArray(sessions)) {
          console.log('[DEBUG] Retrieved cached training sessions:', sessions.length);
          return sessions;
        }
      }
    } catch (e) {
      console.error("Failed to parse cached training sessions:", e);
    }
    return null;
  };
  
  const isAdminUser = authService.isAdmin();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSendingSMS, setIsSendingSMS] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [isPlayerManager, setIsPlayerManager] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [playerID, setPlayerID] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [showSeasonWrappedModal, setShowSeasonWrappedModal] = useState(false);
  const [showSeasonWrappedDirect, setShowSeasonWrappedDirect] = useState(false);
  const [showSeasonWrappedProModal, setShowSeasonWrappedProModal] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  // Use API response data for Pro status instead of authService.isPro() for more accurate results
  const isProFromAPI = subscriptionStatus?.is_pro === true || subscriptionStatus?.has_active_subscription === true;
  const isPro = isProFromAPI;
  const [hasTraining, setHasTraining] = useState(true);
  const [isSuperAdminViewing, setIsSuperAdminViewing] = useState(false);
  const [teamName, setTeamName] = useState('');
  const isSuperAdmin = authService.isSuperAdmin();
  const { showSuccess, showError } = useNotification();
  const [teamAgeGroup, setTeamAgeGroup] = useState(null);
  const [pendingPlayers, setPendingPlayers] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [playersWithParents, setPlayersWithParents] = useState(0);
  const welcomeMessageShown = useRef(false);
  const [showDummyData, setShowDummyData] = useState(false);
  const [apiResponsesReceived, setApiResponsesReceived] = useState({
    fixtures: null,
    results: null,
    training: null
  });
  
  // Parent invite section state
  const [inviteExpanded, setInviteExpanded] = useState(false);
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRelationship, setInviteRelationship] = useState('');
  const [childName, setChildName] = useState('');
  const [canRespondToMatches, setCanRespondToMatches] = useState(false);

  const MINIMUM_SQUAD_PLAYERS = 6;

  const renderChecklistIcon = (completed) => (
    <span className="check-icon" aria-label={completed ? 'Completed' : 'Not completed'}>
      {completed && <FaCheck />}
    </span>
  );

  const renderChecklistCompleteLabel = (completed) =>
    completed ? (
      <span className="check-status">
        <FaCheck size={12} />
        Complete
      </span>
    ) : null;

  // Detect real training on the team (walkthrough complete / match-creation bypass)
  const hasRealTrainingSessions = useMemo(() => {
    if (!hasTraining) return false;
    if (trainingSessions.length > 0) return true;
    const cached = getCachedTrainingSessions();
    if (cached && cached.length > 0) return true;
    if (allMatches.some((event) => event.type === 'training')) return true;
    return false;
  }, [hasTraining, trainingSessions, allMatches]);

  useEffect(() => {
    checkAuthAndRedirect(navigate);

    // Initialize player ID and manager status
    const playerIDValue = sessionStorage.getItem('player_id');
    const isManagerValue = authService.isManager();
    setPlayerID(playerIDValue);
    setIsManager(isManagerValue);

    // Note: Season Wrapped modal logic moved to separate useEffect that depends on subscriptionStatus

    // Store selected team ID in component state to ensure it's tracked properly
    const selectedTeamId = sessionStorage.getItem('selected_team_id');
    setTeamId(selectedTeamId);

    // Check if the team has training enabled
    const teamHasTraining = sessionStorage.getItem('selected_team_has_training');
    setHasTraining(teamHasTraining === 'true' || teamHasTraining === '1');

    // Check if superadmin is viewing as player
    const superadminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
    setIsSuperAdminViewing(superadminViewing && isSuperAdmin);
    
    // Get team name
    const selectedTeamName = sessionStorage.getItem('selected_team_name');
    setTeamName(selectedTeamName || '');
    
    // Get team age group
    const ageGroup = sessionStorage.getItem('selected_team_age_group');
    setTeamAgeGroup(ageGroup);

    // Fetch subscription status
    const fetchSubscriptionStatus = async () => {
      try {
        const token = sessionStorage.getItem('token');
        const teamId = sessionStorage.getItem('selected_team_id');
        const url = teamId 
          ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
          : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
        
        const response = await api.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          setSubscriptionStatus(response.data);
        }
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      }
    };

    // Fetch current season
    const fetchCurrentSeason = async () => {
      // Prevent multiple calls
      if (seasonDataFetchedRef.current) {
        return;
      }
      
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        if (!teamId) return;
        
        seasonDataFetchedRef.current = true; // Mark as fetched
        
        const token = sessionStorage.getItem('token');
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/admin/get_seasons.php?team_id=${teamId}&include_active=true`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        const result = response.data;
        
        if (result.success) {
          // Check if team has any previous seasons (more than just the current one, or any closed seasons)
          const hasPrevious = result.seasons && result.seasons.length > 1 || 
                              result.seasons?.some(s => s.is_active === '0' || s.is_active === 0);
          setHasPreviousSeasons(hasPrevious || false);
          
          // Set current season ID
          if (result.current_season) {
            setCurrentSeasonId(result.current_season.season_id);
          } else if (result.seasons && result.seasons.length > 0) {
            // Find active season or use most recent
            const activeSeason = result.seasons.find(s => s.is_active === 1 || s.is_active === '1');
            if (activeSeason) {
              setCurrentSeasonId(activeSeason.season_id);
            } else if (result.seasons.length > 0) {
              setCurrentSeasonId(result.seasons[0].season_id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching current season:', error);
        seasonDataFetchedRef.current = false; // Reset on error so it can retry
      } finally {
        setSeasonLoaded(true);
      }
    };

    fetchSubscriptionStatus();
    fetchCurrentSeason();

    // Add session storage debugging
    console.log("Dashboard: Current session storage data:", {
      user_id: sessionStorage.getItem('user_id'),
      player_id: sessionStorage.getItem('player_id'),
      selected_team_id: selectedTeamId,
      selected_team_name: selectedTeamName,
      selected_team_has_training: teamHasTraining,
      selected_team_age_group: ageGroup,
      user_type: sessionStorage.getItem('user_type'),
      is_parent: sessionStorage.getItem('is_parent'),
      superadmin_viewing_as_player: superadminViewing
    });

    const checkFirstLogin = () => {
      const isFirstLogin = sessionStorage.getItem('is_first_login') === 'true';
      const userType = sessionStorage.getItem('user_type');
      const isParentUser = sessionStorage.getItem('is_parent') === 'true';
      
      // Check if welcome modal has already been dismissed in this session
      const welcomeModalDismissed = sessionStorage.getItem('welcome_modal_dismissed') === 'true';
      
      // More detailed debugging
      console.log("Dashboard user check:", { 
        userType, 
        playerID: playerIDValue, 
        isManager: isManagerValue, 
        isParent: isParentUser,
        authServiceManagerResult: authService.isManager(),
        isManagerSessionVar: sessionStorage.getItem('is_manager') === 'true'
      });
      
      // Set player-manager status - only if they are both a player AND a manager
      setIsPlayerManager(playerIDValue && isManagerValue);
      console.log("Dashboard: Player-Manager status check:", { 
        isManager: isManagerValue, 
        playerID: playerIDValue, 
        isPlayerManager: playerIDValue && isManagerValue 
      });
      
      // Set coach status
      setIsCoach(userType === 'coach');
      
      // Set parent status
      setIsParent(isParentUser);
      
      if (isFirstLogin && !welcomeModalDismissed) {
        setIsFirstLogin(true);
        setShowWelcomeModal(true);
        
        // Fetch player name if they are a player
        if (playerIDValue) {
          fetchPlayerName(playerIDValue);
        }
      }
    };
    
    const fetchPlayerName = async (playerId) => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_player_profile.php?player_id=${playerId}`);
        if (response.data.success && response.data.player) {
          // Capitalize the first letter of the first name
          const name = response.data.player.first_name;
          setFirstName(name.charAt(0).toUpperCase() + name.slice(1));
        }
      } catch (error) {
        console.error('Error fetching player name:', error);
      }
    };

    const fetchChildName = async () => {
      try {
        const parentId = sessionStorage.getItem('user_id');
        if (parentId) {
          const response = await api.get(`${process.env.REACT_APP_API_URL}/get_child_access_permissions.php?parent_id=${parentId}`);
          if (response.data.success && response.data.permission?.child_name) {
            setChildName(response.data.permission.child_name);
          }
        }
      } catch (error) {
        console.error('Error fetching child name:', error);
      }
    };

    checkFirstLogin();
    
    // Fetch child name if user is a parent
    if (sessionStorage.getItem('is_parent') === 'true') {
      fetchChildName();
    }

    const fetchData = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const isSuperAdminViewing = sessionStorage.getItem('superadmin_viewing_as_player') === 'true';
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
    
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_invite.php?team_id=${teamId}`;
        
        // If a superadmin is viewing as a player, add the player_id as a special parameter
        if (isSuperAdminViewing && isSuperAdmin && playerId) {
          apiUrl += `&superadmin_view_as_player_id=${playerId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;
        
        // We've received fixture data - update state based on whether it has matches or not
        const hasFixtures = result.success && result.matches && result.matches.length > 0;
        console.log("[DEBUG] Fixtures API response:", { 
          success: result.success,
          matchCount: result.matches?.length || 0,
          hasFixtures,
          fullResult: result
        });
        
        setApiResponsesReceived(prev => {
          const newState = { ...prev, fixtures: hasFixtures };
  
          return newState;
        });
        
        if (result.success) {
          // Get current date
          const now = new Date();
          
          // Upcoming fixtures only (no upper date limit — matches get_invite.php)
          const upcomingMatches = result.matches.filter(match => {
            // Check if this is a TBC match
            const isTBC = match.status === 'TBC' || match.match_time === '00:00:00' || match.match_time === '00:00';
            
            if (isTBC) {
              const matchDate = new Date(match.match_date);
              return matchDate >= now;
            } else {
              const matchDateTime = new Date(`${match.match_date}T${match.match_time}`);
              return matchDateTime > now;
            }
          }).sort((a, b) => {
            // Handle TBC matches in sorting
            const aIsTBC = a.status === 'TBC' || a.match_time === '00:00:00' || a.match_time === '00:00';
            const bIsTBC = b.status === 'TBC' || b.match_time === '00:00:00' || b.match_time === '00:00';
            
            // First sort by date
            const dateA = new Date(a.match_date);
            const dateB = new Date(b.match_date);
            if (dateA.getTime() !== dateB.getTime()) {
              return dateA - dateB;
            }
            
            // If same date, TBC matches go after regular matches
            if (aIsTBC && !bIsTBC) return 1;
            if (!aIsTBC && bIsTBC) return -1;
            if (aIsTBC && bIsTBC) return 0;
            
            // Otherwise sort by time
            const timeA = new Date(`${a.match_date}T${a.match_time}`);
            const timeB = new Date(`${b.match_date}T${b.match_time}`);
            return timeA - timeB;
          });
          
          setFutureMatches(upcomingMatches);

          
          // Set all matches for the calendar
          setAllMatches(result.matches);
        }
      } catch (error) {
        // Mark that we received a response but there was an error
        console.error('[DEBUG] Error fetching fixtures:', error);
        setApiResponsesReceived(prev => {
          const newState = { ...prev, fixtures: false };
  
          return newState;
        });
      }
    };

    const fetchMatchResults = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';

        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_match_results.php?team_id=${teamId}`;
        
        // Add current season_id if available
        if (currentSeasonId) {
          apiUrl += `&season_id=${currentSeasonId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;
        
        // We've received match results data - update state based on whether it has matches or not
        const hasResults = result.success && result.matches && result.matches.length > 0;

        
        setApiResponsesReceived(prev => {
          const newState = { ...prev, results: hasResults };

          return newState;
        });
        
        if (result.success) {
          // The API already filters to only completed matches, so we can use all results
          setMatchResults(result.matches);
          setRecentMatches(result.matches);

        } else {
          console.error('[DEBUG] Failed to fetch match results');
        }
      } catch (error) {
        // Mark that we received a response but there was an error
        console.error('[DEBUG] Error fetching match results:', error);
        setApiResponsesReceived(prev => {
          const newState = { ...prev, results: false };

          return newState;
        });
      }
    };

    const fetchTrainingData = async () => {
      try {
        // Use component state or get fresh value from sessionStorage
        const teamId = selectedTeamId || sessionStorage.getItem('selected_team_id');
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Add validation - if teamId is still null, we'll retry
        if (!teamId) {
          console.log("[DEBUG] TeamID is null for fetchTrainingData, will retry shortly");
          setApiResponsesReceived(prev => {
            const newState = { ...prev, training: null };
            console.log("[DEBUG] Updated apiResponsesReceived state after training null teamId:", newState);
            return newState;
          });
          return;
        }
        
        console.log("[DEBUG] Fetching training data for teamId:", teamId);
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/get_training_sessions.php?team_id=${teamId}`;
        
        // Always add the player_id parameter for responder information
        if (playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const result = response.data;
        
        // Check if we have training data
        const hasTrainingSessions = result.success && result.sessions && result.sessions.length > 0;
        console.log("[DEBUG] Training API response:", { 
          success: result.success,
          sessionsCount: result.sessions?.length || 0,
          hasTrainingSessions
        });
        
        // Store the training sessions in state if available
        if (hasTrainingSessions) {
          // Sort sessions by date (ascending) to get the next upcoming session first
          const sortedSessions = result.sessions.sort((a, b) => {
            return new Date(a.match_date + 'T' + a.match_time) - new Date(b.match_date + 'T' + b.match_time);
          });
          
          // Filter out past sessions
          const now = new Date();
          const futureSessions = sortedSessions.filter(session => {
            const sessionDate = new Date(session.match_date + 'T' + session.match_time);
            return sessionDate >= now;
          });
          
          // Update trainingSessions state
          setTrainingSessions(futureSessions);
          trainingDataFetchedRef.current = true;
          
          // Store in sessionStorage as a backup
          try {
            sessionStorage.setItem('training_sessions', JSON.stringify(futureSessions));
          } catch (e) {
            console.error("Failed to store training sessions in sessionStorage:", e);
          }
          
          // Add training sessions to allMatches with type='training'
          const trainingWithType = futureSessions.map(session => ({
            ...session,
            type: 'training'
          }));
          
          // Merge with existing matches
          setAllMatches(prevMatches => {
            // Filter out any existing training sessions to avoid duplicates
            const matchesOnly = prevMatches.filter(match => match.type !== 'training');
            return [...matchesOnly, ...trainingWithType];
          });
          
          // Force a re-render
          rerender();
          
          console.log("[DEBUG] Set trainingSessions:", futureSessions.length, "future sessions from", result.sessions.length, "total");
        } else {
          setTrainingSessions([]);
          trainingDataFetchedRef.current = true;
          
          // Store empty array in sessionStorage
          try {
            sessionStorage.setItem('training_sessions', JSON.stringify([]));
          } catch (e) {
            console.error("Failed to store empty training sessions in sessionStorage:", e);
          }
          
          // Force a re-render
          rerender();
        }
        
        // Update our tracking state
        setApiResponsesReceived(prev => {
          const newState = { ...prev, training: hasTrainingSessions };
          console.log("[DEBUG] Updated apiResponsesReceived state after training:", newState);
          return newState;
        });
      } catch (error) {
        console.error('[DEBUG] Error fetching training sessions:', error);
        setTrainingSessions([]);
        trainingDataFetchedRef.current = true;
        
        // Force a re-render
        rerender();
        
        setApiResponsesReceived(prev => {
          const newState = { ...prev, training: false };
          console.log("[DEBUG] Updated apiResponsesReceived state after training error:", newState);
          return newState;
        });
      }
    };

    // Load data in sequence with a slight delay to ensure team_id is available
    fetchData();
    // Past results (Recent Matches + Last 5) load in the currentSeasonId effect via fetchLastResults
    
    // Add a retry mechanism for training data if team has training enabled OR if user is a parent
    const userTypeForTraining = sessionStorage.getItem('user_type');
    const isParentUser = userTypeForTraining === 'parent' || sessionStorage.getItem('is_parent') === 'true';
    
    if (teamHasTraining !== 'false' || isParentUser) {
      // First attempt immediately
      fetchTrainingData();
      
      // Second attempt after a short delay 
      setTimeout(() => {
        const currentTeamId = sessionStorage.getItem('selected_team_id');
        if (currentTeamId && apiResponsesReceived.training === null) {
          console.log("[DEBUG] Retrying fetchTrainingData with teamId:", currentTeamId);
          fetchTrainingData();
        }
      }, 1000);
    } else {
      // If training is disabled, mark it as received but false
      setApiResponsesReceived(prev => ({...prev, training: false}));
    }
  }, [navigate, isSuperAdmin, currentSeasonId]);

  // Removed automatic Season Wrapped popup on login
  // Users can now access Season Wrapped via the dashboard button instead
  // This prevents non-pro players from seeing payment popups immediately upon login

  // Add a new effect to retry fetching training sessions if team_id wasn't available initially
  useEffect(() => {
    // Only run this effect if:
    // 1. We have a teamId in state
    // 2. Training is enabled OR user is a parent
    // 3. We still don't have training data (apiResponsesReceived.training is null)
    const hasTrainingEnabled = sessionStorage.getItem('selected_team_has_training') === 'true' || sessionStorage.getItem('selected_team_has_training') === '1';
    const userType = sessionStorage.getItem('user_type');
    const isParentUser = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
    
    if (teamId && (hasTrainingEnabled || isParentUser) && apiResponsesReceived.training === null) {
      console.log("[DEBUG] Running retry effect for fetchTrainingData with teamId:", teamId);
      
      const fetchTrainingData = async () => {
        try {
          const playerId = sessionStorage.getItem('player_id');
          const userType = sessionStorage.getItem('user_type');
          const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
          
          console.log("[DEBUG] Retry: Fetching training data for teamId:", teamId);
          
          // Build the API URL
          let apiUrl = `${process.env.REACT_APP_API_URL}/get_training_sessions.php?team_id=${teamId}`;
          
          // If user is a parent, add the child_id parameter
          if (isParent && playerId) {
            apiUrl += `&child_id=${playerId}`;
          }
          
          const response = await api.get(apiUrl);
          const result = response.data;
          
          // Check if we have training data
          const hasTrainingSessions = result.success && result.sessions && result.sessions.length > 0;
          console.log("[DEBUG] Retry: Training API response:", { 
            success: result.success,
            sessionsCount: result.sessions?.length || 0,
            hasTrainingSessions
          });
          
          // Store the training sessions in state if available
          if (hasTrainingSessions) {
            // Sort sessions by date (ascending) to get the next upcoming session first
            const sortedSessions = result.sessions.sort((a, b) => {
              return new Date(a.match_date + 'T' + a.match_time) - new Date(b.match_date + 'T' + b.match_time);
            });
            
            // Filter out past sessions
            const now = new Date();
            const futureSessions = sortedSessions.filter(session => {
              const sessionDate = new Date(session.match_date + 'T' + session.match_time);
              return sessionDate >= now;
            });
            
            // Update trainingSessions state
            setTrainingSessions(futureSessions);
            trainingDataFetchedRef.current = true;
            
            // Store in sessionStorage as a backup
            try {
              sessionStorage.setItem('training_sessions', JSON.stringify(futureSessions));
            } catch (e) {
              console.error("Failed to store training sessions in sessionStorage:", e);
            }
            
            // Add training sessions to allMatches with type='training'
            const trainingWithType = futureSessions.map(session => ({
              ...session,
              type: 'training'
            }));
            
            // Merge with existing matches
            setAllMatches(prevMatches => {
              // Filter out any existing training sessions to avoid duplicates
              const matchesOnly = prevMatches.filter(match => match.type !== 'training');
              return [...matchesOnly, ...trainingWithType];
            });
            
            // Force a re-render
            rerender();
            
            console.log("[DEBUG] Retry: Set trainingSessions:", futureSessions.length, "future sessions from", result.sessions.length, "total");
          } else {
            setTrainingSessions([]);
            trainingDataFetchedRef.current = true;
            
            // Store empty array in sessionStorage
            try {
              sessionStorage.setItem('training_sessions', JSON.stringify([]));
            } catch (e) {
              console.error("Failed to store empty training sessions in sessionStorage:", e);
            }
            
            // Force a re-render
            rerender();
          }
          
          // Update our tracking state
          setApiResponsesReceived(prev => {
            const newState = { ...prev, training: hasTrainingSessions };
            console.log("[DEBUG] Retry: Updated apiResponsesReceived state after training:", newState);
            return newState;
          });
        } catch (error) {
          console.error('[DEBUG] Retry: Error fetching training sessions:', error);
          setTrainingSessions([]);
          trainingDataFetchedRef.current = true;
          
          // Force a re-render
          rerender();
          
          setApiResponsesReceived(prev => {
            const newState = { ...prev, training: false };
            console.log("[DEBUG] Retry: Updated apiResponsesReceived state after training error:", newState);
            return newState;
          });
        }
      };
      
      fetchTrainingData();
    }
  }, [teamId, apiResponsesReceived.training]);

  useEffect(() => {
    const checkAchievements = async () => {
      try {
        const playerId = sessionStorage.getItem('player_id');
        const teamId = sessionStorage.getItem('selected_team_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        // Build the API URL
        let apiUrl = `${process.env.REACT_APP_API_URL}/check_achievements.php?player_id=${playerId}&team_id=${teamId}`;
        
        // Add current season_id if available
        if (currentSeasonId) {
          apiUrl += `&season_id=${currentSeasonId}`;
        }
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          apiUrl += `&child_id=${playerId}`;
        }
        
        const response = await api.get(apiUrl);
        const data = response.data;
        
        if (data.success && data.unannounced_achievement) {
          const achievementData = achievements.find(a => a.id === data.unannounced_achievement);
          if (achievementData) {
            setCurrentAchievement(achievementData);
            // Delay showing the modal
            setTimeout(() => {
              setShowAchievement(true);
            }, 1500);
          }
        }
      } catch (error) {
        console.error('Error checking achievements:', error);
      }
    };

    checkAchievements();
  }, [currentSeasonId]);

  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        const buildResultsUrl = (seasonId) => {
          let apiUrl = `${process.env.REACT_APP_API_URL}/get_match_results.php?team_id=${teamId}`;
          if (seasonId) {
            apiUrl += `&season_id=${seasonId}`;
          }
          if (isParent && playerId) {
            apiUrl += `&child_id=${playerId}`;
          }
          return apiUrl;
        };

        let result = (await api.get(buildResultsUrl(currentSeasonId))).data;

        // Switching academy/squad can leave two active seasons. If the
        // dashboard season has no results, show past matches without that filter.
        if ((!result.success || !result.matches || result.matches.length === 0) && currentSeasonId) {
          const fallback = (await api.get(buildResultsUrl(null))).data;
          if (fallback.success && fallback.matches && fallback.matches.length > 0) {
            result = fallback;
          }
        }
        
        // We've received match results data - update state based on whether it has matches or not
        const hasResults = result.success && result.matches && result.matches.length > 0;

        
        setApiResponsesReceived(prev => {
          const newState = { ...prev, results: hasResults };

          return newState;
        });
        
        if (result.success && result.matches && result.matches.length > 0) {
          // Format the data to match what we need for the last results section
          // The API already filters to only completed matches, so we can use all results
          const formattedResults = result.matches
            .map(match => {
              // Get the current team name from session
              const currentTeamName = sessionStorage.getItem('selected_team_name') || 'Team MNF';
              
              let team_a, team_b, homeScore, awayScore;
              
              // Check if opposition is "Whites vs Colours" (placeholder) - use team_a and team_b from API
              if (match.opposition === 'Whites vs Colours' && match.team_a && match.team_b) {
                team_a = match.team_a;
                team_b = match.team_b;
                // Use score_white for team_a and score_colour for team_b
                homeScore = match.score_white !== null ? match.score_white : '-';
                awayScore = match.score_colour !== null ? match.score_colour : '-';

              } else {
                // Use existing logic for determining home/away teams
                const isHome = match.is_home === 1 || match.is_home === true || match.is_home === '1';
                console.log(`Match ${match.match_id}: is_home = ${match.is_home}, isHome = ${isHome}, opposition = ${match.opposition}`);
                
                team_a = isHome ? currentTeamName : match.opposition;
                team_b = isHome ? match.opposition : currentTeamName;
                homeScore = isHome ? (match.score_white !== null ? match.score_white : '-') : (match.score_colour !== null ? match.score_colour : '-');
                awayScore = isHome ? (match.score_colour !== null ? match.score_colour : '-') : (match.score_white !== null ? match.score_white : '-');
              }
              
              return {
                match_id: match.match_id,
                match_date: match.date,
                white_score: homeScore,
                colour_score: awayScore,
                player_team: match.colour || match.team_color || '', // This is the player's team for this match
                goals_scored: match.goalScored || '0',
                location: match.location,
                team_a: team_a,
                team_b: team_b,
                opposition: match.opposition,
                is_home: match.is_home === 1 || match.is_home === true || match.is_home === '1'
              };
            });
          
          setLastResults(formattedResults.slice(0, 5)); // Take only the first 5
          setRecentMatches(result.matches);
          setMatchResults(result.matches);
        } else {
          setLastResults([]);
          setRecentMatches([]);
          setMatchResults([]);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
        setLastResults([]);
        setRecentMatches([]);
        setMatchResults([]);
        // Mark that we received a response but there was an error
        setApiResponsesReceived(prev => {
          const newState = { ...prev, results: false };

          return newState;
        });
      }
    };

    if (currentSeasonId !== null) {
      fetchLastResults();
    }
  }, [currentSeasonId]);

  useEffect(() => {
    const fetchPendingPlayers = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        if (!teamId || (!isAdminUser && !isCoach && !isPlayerManager)) return;

        const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=pending`);
        if (response.data.success) {
          setPendingPlayers(response.data.total_players || 0);
        }
      } catch (error) {
        console.error('Error fetching pending players:', error);
      }
    };

    const fetchTotalPlayers = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        if (!teamId || (!isAdminUser && !isCoach && !isPlayerManager)) return;

        const response = await api.get(`${process.env.REACT_APP_API_URL}/admin_get_players.php?team_id=${teamId}&filter=players`);
        if (response.data.success) {
          setTotalPlayers(response.data.total_players || 0);
          
          // Count players with at least one parent
          if (response.data.players && Array.isArray(response.data.players)) {
            const playersWithParentsCount = response.data.players.filter(player => {
              try {
                if (!player.parent_data) return false;
                const parentData = typeof player.parent_data === 'string' 
                  ? JSON.parse(player.parent_data) 
                  : player.parent_data;
                // Handle both array and single object format
                const parents = Array.isArray(parentData) ? parentData : [parentData];
                return parents.length > 0 && parents[0] !== null;
              } catch (e) {
                return false;
              }
            }).length;
            setPlayersWithParents(playersWithParentsCount);
          }
        }
      } catch (error) {
        console.error('Error fetching total players:', error);
      }
    };

    fetchPendingPlayers();
    fetchTotalPlayers();
  }, [isAdminUser, isCoach, isPlayerManager]);

  const handleRespond = async (matchId, response, isWaitlist) => {
    try {
      const playerId = sessionStorage.getItem('player_id');
      
      // Check if parent has permission to respond to matches for this child
      if (authService.isParent()) {
        const canRespond = authService.canRespondToMatches(playerId);
        console.log('Parent permission check:', {
          isParent: authService.isParent(),
          playerId: playerId,
          canRespond: canRespond
        });
        
        if (!canRespond) {
          showError('You do not have permission to respond to matches for this child.');
          return;
        }
      }
      const res = await api.post(`${process.env.REACT_APP_API_URL}/update_player_response.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          player_id: playerId,
          response: response,
          match_id: matchId,
          is_waitlist: isWaitlist ? 1 : 0
        })
      });

      // API response is already parsed in the api service
      const data = res.data && typeof res.data === 'object' ? res.data : null;
      
      if (data?.success) {
        notifyAvailabilityResponseSaved();
        // Check if this is a training session
        const isTraining = data.is_training || false;
        
        // Show success notification based on response type and session type
        if (response === 'yes') {
          if (isWaitlist) {
            showSuccess('You have been added to the waiting list. We\'ll let you know if a spot becomes available.');
          } else if (isTraining) {
            showSuccess('Thanks for confirming! We look forward to seeing you at training.');
          } else {
            showSuccess('Thanks for confirming! We look forward to seeing you at the match.');
          }
        } else if (response === 'no') {
          if (isTraining) {
            showSuccess('Response updated. We hope to see you at future training sessions.');
          } else {
            showSuccess('Response updated. We hope to see you at a future match.');
          }
        }
        
        // Update the local state to reflect the change
        setFutureMatches(prevMatches => 
          prevMatches.map(match => 
            match.match_id === matchId 
              ? { ...match, response: response.charAt(0).toUpperCase() + response.slice(1), is_waitlist: isWaitlist }
              : match
          )
        );

        // Also update allMatches for the calendar
        setAllMatches(prevMatches => 
          prevMatches.map(match => 
            match.match_id === matchId 
              ? { ...match, response: response.charAt(0).toUpperCase() + response.slice(1), is_waitlist: isWaitlist }
              : match
          )
        );
        
        // Update training sessions state if this was a training response
        if (isTraining) {
          setTrainingSessions(prevSessions => 
            prevSessions.map(session => 
              session.match_id === matchId 
                ? { 
                    ...session, 
                    response: response.charAt(0).toUpperCase() + response.slice(1),
                    response_updated: new Date().toISOString(),
                    responder_first_name: sessionStorage.getItem('first_name') || 'Parent',
                    responder_last_name: sessionStorage.getItem('last_name') || ''
                  }
                : session
            )
          );
        }
      } else {
        console.error('Failed to update response:', data?.message);
        showError('Failed to update response: ' + (data?.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error updating response:', error);
      showError('There was an error updating your response. Please try again.');
    }
  };

  const handleAssignTeams = (matchId) => {
    navigate(`/teamSelection/${matchId}`);
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleWelcomeModalDismiss = () => {
    // Set session storage to remember that the welcome modal has been dismissed
    sessionStorage.setItem('welcome_modal_dismissed', 'true');
    setShowWelcomeModal(false);
  };

  const handleAchievementClose = async () => {
    if (currentAchievement) {
      try {
        const playerId = sessionStorage.getItem('player_id');
        const userType = sessionStorage.getItem('user_type');
        const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
        
        const requestData = {
          player_id: playerId,
          achievement: currentAchievement.id
        };
        
        // If user is a parent, add the child_id parameter
        if (isParent && playerId) {
          requestData.child_id = playerId;
        }
        
        await fetch(`${process.env.REACT_APP_API_URL}/mark_achievement_seen.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } catch (error) {
        console.error('Error marking achievement as seen:', error);
      }
    }
    setShowAchievement(false);
  };

  // Generate an array of dates for the next 30 days
  const generateDates = () => {
    const dates = [];
    const today = new Date();
    today.setDate(today.getDate() - 14); // Start 14 days before today
    for (let i = 0; i < 44; i++) { // 30 days ahead + 14 days before
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const dateString = date.toISOString().split('T')[0];
        // Add a unique index to any potential duplicate dates
        const uniqueId = `${dateString}-${i}`;
        dates.push(uniqueId);
    }
    return dates;
  };

  const handleMatchDeleted = (matchId) => {
    setFutureMatches(currentMatches => currentMatches.filter(match => match.match_id !== matchId));
    setAllMatches(currentMatches => currentMatches.filter(match => match.match_id !== matchId));
  };

  const generateInviteCode = () => {
    let sum = 10;
    const code = [];
    const teamId = sessionStorage.getItem('selected_team_id') || '1';

    // Generate the 4-digit code that sums to 10
    for (let i = 0; i < 3; i++) {
      const digit = Math.floor(Math.random() * (sum - (3 - i))) + 1;
      code.push(digit);
      sum -= digit;
    }
    code.push(sum);

    // Shuffle the code array
    for (let i = code.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [code[i], code[j]] = [code[j], code[i]];
    }

    // Format with team ID prefix
    const fullCode = `${teamId}-${code.join('')}`;
    setInviteCode(fullCode);
    console.log('Generated Invite Code:', fullCode);
  };

  const copyToClipboard = () => {
    // Remove any trailing slashes from the public URL
    const baseUrl = process.env.REACT_APP_PUBLIC_URL.replace(/\/+$/, '');
    const url = `${baseUrl}/register?code=${inviteCode}`;
    navigator.clipboard.writeText(url).then(() => {
      showSuccess('Invite link copied to clipboard!');
    }).catch(err => {
      console.error('Could not copy text: ', err);
      showError('Failed to copy to clipboard. Please try again.');
    });
  };

  const handleOpenInviteModal = () => {
    if (isYouthTeam() || sessionStorage.getItem('selected_team_type') === 'friendly') {
      handleNavigate('/players');
      return;
    }
    
    generateInviteCode();
    setPhoneNumber('');
    setSmsSent(false);
    setPhoneError('');
    setShowInviteModal(true);
  };
  
  const validatePhoneNumber = (phone) => {
    // Basic UK phone number validation - allows various formats
    const phoneRegex = /^(\+44|0)?[7-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\s+/g, ''));
  };
  
  const formatPhoneNumber = (phone) => {
    // Format phone number for sending - ensure it starts with +44
    let formatted = phone.replace(/\s+/g, '');
    
    if (formatted.startsWith('0')) {
      formatted = '+44' + formatted.substring(1);
    } else if (!formatted.startsWith('+44')) {
      formatted = '+44' + formatted;
    }
    
    return formatted;
  };
  
  const sendSMSInvite = async () => {
    // Validate phone number
    if (!phoneNumber) {
      setPhoneError('Please enter a phone number');
      return;
    }
    
    if (!validatePhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid UK mobile number');
      return;
    }
    
    setPhoneError('');
    setIsSendingSMS(true);
    
    try {
      // Format the SMS message
      const url = `${process.env.REACT_APP_PUBLIC_URL}/register?code=${inviteCode}`;
      const teamName = sessionStorage.getItem('selected_team_name') || 'our team';
      const message = `Hi there! You've been invited to join ${teamName} on FirstWhistle. Use this link to register: ${url}`;
      
      // Format the phone number 
      const formattedPhone = formatPhoneNumber(phoneNumber);
      
      // Test mode - always send to the specified test number for now
      const testNumber = '07875930733';
      
      // Send the SMS using the API endpoint
      const response = await api.post(`${process.env.REACT_APP_API_URL}/send_sms.php`, {
        phone_number: testNumber, // Use test number for now
        message: message
      });
      
      // Handle the response - PHP returns true (boolean) not a JSON object with success property
      // The PHP function returns either true or false directly
      console.log('SMS API response:', response.data);
      
      if (
        response.data === true || 
        response.data === "true" || 
        response.data === 1 || 
        response.data === "1"
      ) {
        setSmsSent(true);
        console.log('SMS sent successfully');
        
        // Clear success message and reset phone field after 5 seconds
        setTimeout(() => {
          setSmsSent(false);
          setPhoneNumber('');
        }, 5000);
      } else {
        setPhoneError('Failed to send SMS. Please try again or share the code manually.');
        console.error('SMS sending failed', response.data);
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      setPhoneError('Error sending SMS. Please try again later.');
    } finally {
      setIsSendingSMS(false);
    }
  };

  // Validate if user is trying to invite their child
  const validateChildInvite = () => {
    const childName = sessionStorage.getItem('child_name');
    if (childName && inviteFirstName.trim() && inviteLastName.trim()) {
      const childNameParts = childName.trim().split(' ');
      const childFirstName = childNameParts[0]?.toLowerCase();
      const childLastName = childNameParts[childNameParts.length - 1]?.toLowerCase();
      
      if (inviteFirstName.toLowerCase() === childFirstName && 
          inviteLastName.toLowerCase() === childLastName) {
        showError('It looks like you\'re trying to invite your child. Please use "Child Access Settings" instead to give your child access to their own account.');
        return true;
      }
    }
    return false;
  };

  // Handle SMS invitation for parent invite section
  const handleSendSMSInvite = async () => {
    // Validate required fields
    if (!inviteFirstName.trim()) {
      showError('Please enter a first name');
      return;
    }

    if (!inviteLastName.trim()) {
      showError('Please enter a last name');
      return;
    }
    
    if (!inviteRelationship.trim()) {
      showError('Please select a relationship');
      return;
    }

    if (!smsPhoneNumber.trim()) {
      showError('Please enter a mobile number');
      return;
    }

    if (!validatePhoneNumber(smsPhoneNumber)) {
      showError('Please enter a valid UK mobile number');
      return;
    }

    // Client-side validation: Check if trying to invite the child
    if (validateChildInvite()) {
      return;
    }

    setIsSendingSMS(true);

    try {
      const teamId = sessionStorage.getItem('selected_team_id');
      const playerId = sessionStorage.getItem('player_id');
      const childFirstName = childName?.split(' ')[0] || 'your child';
      
      // Use the invite_parent.php API to create the relationship
      const response = await api.post(`${process.env.REACT_APP_API_URL}/invite_parent.php`, {
        first_name: inviteFirstName,
        last_name: inviteLastName,
        phone: smsPhoneNumber,
        child_id: playerId,
        relationship_type: 'secondary',
        relationship: inviteRelationship,
        is_legal_guardian: 0,
        can_respond_to_matches: canRespondToMatches ? 1 : 0
      });
      
      if (response.data.success) {
        showSuccess('Extended family member invited successfully!');
        // Reset form fields
        setInviteFirstName('');
        setInviteLastName('');
        setInviteRelationship('');
        setSmsPhoneNumber('');
        setCanRespondToMatches(true);
      } else {
        showError(response.data.message || 'Failed to send invitation. Please try again.');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      showError('Error sending invitation. Please try again later.');
    } finally {
      setIsSendingSMS(false);
    }
  };

  // Handle confetti effect
  const triggerConfetti = () => {
    const duration = 2000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1500 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Create confetti bursts at various positions
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.2, 0.4), y: randomInRange(0.2, 0.4) }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.6, 0.8), y: randomInRange(0.2, 0.4) }
      });
    }, 250);
  };
  
  // Trigger confetti when welcome modal is shown
  useEffect(() => {
    if (showWelcomeModal) {
      triggerConfetti();
    }
  }, [showWelcomeModal]);



  // Auto-refresh fixtures every minute to hide past matches
  useEffect(() => {
    const interval = setInterval(() => {
      if (allMatches.length > 0) {
        const now = new Date();
        
        const upcomingMatches = allMatches.filter(match => {
          const isTBC = match.status === 'TBC' || match.match_time === '00:00:00' || match.match_time === '00:00';
          if (isTBC) {
            return new Date(match.match_date) >= now;
          }
          return new Date(`${match.match_date}T${match.match_time}`) > now;
        }).sort((a, b) => {
          const dateA = new Date(`${a.match_date}T${a.match_time}`);
          const dateB = new Date(`${b.match_date}T${b.match_time}`);
          return dateA - dateB;
        });
        
        setFutureMatches(upcomingMatches);
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [allMatches]);

  // Function to exit superadmin view mode
  const exitSuperAdminView = () => {
    sessionStorage.removeItem('superadmin_viewing_as_player');
    sessionStorage.removeItem('selected_team_id');
    sessionStorage.removeItem('selected_team_name');
    sessionStorage.removeItem('selected_team_type');
    navigate('/team-selection');
  };

  // Helper function to check if age group is less than U13s
  const isYoungerThanU13 = () => {
    if (!teamAgeGroup) return false;
    
    // Extract the age number from the age group string (e.g., "U13s" -> 13)
    const ageMatch = teamAgeGroup.match(/U(\d+)s?/i);
    if (!ageMatch || ageMatch.length < 2) return false;
    
    const age = parseInt(ageMatch[1], 10);
    return age < 13;
  };

  // Create dummy/example data for new users
  const getDummyFixture = () => {
    // Create an array of dummy matches
    const matches = [];
    
    // Get current date
    const today = new Date();
    
    // Generate matches for the next 4 weeks (Saturdays)
    for (let week = 0; week < 4; week++) {
      // Saturday match (day 6 of week, 0-indexed)
      const saturdayDate = new Date(today);
      saturdayDate.setDate(today.getDate() + (6 - today.getDay() + 7) % 7 + (week * 7));
      
      // Different opposition teams for variety
      const oppositionTeams = ['FC United', 'City Rovers', 'Athletic Club', 'United Stars'];
      const locations = ['Home Ground', 'Away Pitch', 'Community Stadium', 'Sports Complex'];
      
      matches.push({
        match_id: `match-saturday-${week + 1}`,
        type: 'match',
        match_date: format(saturdayDate, 'yyyy-MM-dd'),
        match_time: '14:00:00',
        location: locations[week % locations.length],
        max_players: 12,
        yes: [],
        no: [],
        response: 'Invited',
        entry_open: true,
        opposition: oppositionTeams[week % oppositionTeams.length],
        isDummy: true
      });
    }
    
    return matches;
  };
  
  const getDummyRecentMatch = () => {
    // Get a date 7 days ago
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 7);
    
    // Format the date
    const matchDate = format(pastDate, 'yyyy-MM-dd');
    
    return {
      match_id: 1,
      date: matchDate,
      played_match: true,
      score_white: 3,
      score_colour: 2,
      location: 'Local Pitch',
      motm_first_name: 'Example',
      motm_last_name: 'Player',
      goalScored: 1,
      opposition: 'FC United',
      isDummy: true
    };
  };
  
  const getDummyLastResult = () => {
    // Get a date 7 days ago
    const pastDate = new Date();
    pastDate.setDate(pastDate.getDate() - 7);
    
    // Format the date
    const matchDate = format(pastDate, 'yyyy-MM-dd');
    
    return {
      match_id: 1,
      match_date: matchDate,
      white_score: '3',
      colour_score: '2',
      player_team: 'white',
      goals_scored: '1',
      location: 'Local Pitch',
      team_a: 'Whites',
      team_b: 'Colours',
      opposition: 'Colours',
      isDummy: true
    };
  };
  
  // Dummy training data
  const getDummyTraining = () => {
    // Create an array of dummy training sessions
    const trainingSessions = [];
    
    // Get current date
    const today = new Date();
    
    // Generate training sessions for the next 4 weeks (Tuesdays and Thursdays)
    for (let week = 0; week < 4; week++) {
      // Tuesday training (day 2 of week, 0-indexed)
      const tuesdayDate = new Date(today);
      tuesdayDate.setDate(today.getDate() + (2 - today.getDay() + 7) % 7 + (week * 7));
      
      // Thursday training (day 4 of week, 0-indexed)
      const thursdayDate = new Date(today);
      thursdayDate.setDate(today.getDate() + (4 - today.getDay() + 7) % 7 + (week * 7));
      
      // Add Tuesday training
      trainingSessions.push({
        training_id: `training-tuesday-${week + 1}`,
        match_id: `training-tuesday-${week + 1}`,
        match_date: format(tuesdayDate, 'yyyy-MM-dd'),
        match_time: '18:00:00',
        time: '18:00:00',
        location: 'Claverham Community College',
        notes: 'Regular Tuesday training session',
        response: 'Invited',
        total_yes: 7,
        total_no: 2,
        total_awaiting: 3,
        duration: 90,
        isDummy: true
      });
      
      // Add Thursday training
      trainingSessions.push({
        training_id: `training-thursday-${week + 1}`,
        match_id: `training-thursday-${week + 1}`,
        match_date: format(thursdayDate, 'yyyy-MM-dd'),
        match_time: '18:00:00',
        time: '18:00:00',
        location: 'Claverham Community College',
        notes: 'Regular Thursday training session',
        response: 'Invited',
        total_yes: 8,
        total_no: 1,
        total_awaiting: 2,
        duration: 90,
        isDummy: true
      });
    }
    
    return trainingSessions;
  };
  
  // Update the useEffect for showing dummy data
  useEffect(() => {
    // Check if user is a parent - parents should never see dummy data
    const userType = sessionStorage.getItem('user_type');
    const isParentUser = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';
    
    // If user is a parent, never show dummy data
    if (isParentUser) {
      if (showDummyData) {
        setShowDummyData(false);
      }
      return;
    }
    
    // Check if all necessary endpoints have been fetched
    // For fixtures and results, we require explicit API responses
    const allEndpointsFetched = 
      apiResponsesReceived.fixtures !== null && 
      apiResponsesReceived.results !== null;
    
    // Check if there are any training sessions (only if training is enabled)
    const hasTrainingSessions = hasRealTrainingSessions;
    
    // Only show dummy data if:
    // 1. Both main API endpoints returned no data
    // 2. (no training sessions OR training is disabled)
    // 3. Team has NO previous seasons (if they have previous seasons, don't show dummy data)
    const shouldShowDummyData = 
      allEndpointsFetched && 
      apiResponsesReceived.fixtures === false && 
      apiResponsesReceived.results === false &&
      (!hasTrainingSessions || !hasTraining) &&
      !hasPreviousSeasons; // Don't show dummy data if team has previous seasons
    
    // Debug logging removed for cleaner console
    
    // This forces a re-render with the exact value, not a state update that might be batched
    if (showDummyData !== shouldShowDummyData) {

      setShowDummyData(shouldShowDummyData);
    }
  }, [apiResponsesReceived, hasTraining, showDummyData, hasPreviousSeasons, hasRealTrainingSessions]);



  // Prepare the data to display
  // const displayedFutureMatches = showDummyData ? [getDummyFixture()] : futureMatches;
  // const displayedRecentMatches = showDummyData ? [getDummyRecentMatch()] : recentMatches;
  // const displayedLastResults = showDummyData ? [getDummyLastResult()] : lastResults;

  // Create a function to merge matches and training sessions for the calendar
  const getMergedEventsForCalendar = () => {
    // Start with all matches
    const allEvents = [...allMatches];
    
    // Get cached training sessions if needed
    const cachedTrainingSessions = trainingSessions.length === 0 ? getCachedTrainingSessions() : null;
    
    // Add training sessions with type='training'
    if (trainingSessions.length > 0) {
      const trainingWithType = trainingSessions.map(session => ({
        ...session,
        type: 'training'
      }));
      
      // Combine with existing events, avoiding duplicates
      trainingWithType.forEach(session => {
        // Check if this session is already in allEvents
        const exists = allEvents.some(
          event => event.type === 'training' && event.match_id === session.match_id
        );
        
        if (!exists) {
          allEvents.push(session);
        }
      });
    } else if (cachedTrainingSessions) {
      // If no trainingSessions in state but we have cached ones, use those
      const trainingWithType = cachedTrainingSessions.map(session => ({
        ...session,
        type: 'training'
      }));
      
      trainingWithType.forEach(session => {
        // Check if this session is already in allEvents
        const exists = allEvents.some(
          event => event.type === 'training' && event.match_id === session.match_id
        );
        
        if (!exists) {
          allEvents.push(session);
        }
      });
    }
    
    // Add dummy events when showing dummy data
    if (showDummyData) {
      // Add dummy matches
      const dummyMatches = getDummyFixture();
      dummyMatches.forEach(match => {
        const exists = allEvents.some(
          event => event.type === 'match' && event.match_id === match.match_id
        );
        
        if (!exists) {
          allEvents.push(match);
        }
      });
      
      // Add dummy training sessions only if training is enabled
      if (hasTraining) {
        const dummyTrainingSessions = getDummyTraining();
        const dummyTrainingWithType = dummyTrainingSessions.map(session => ({
          ...session,
          type: 'training'
        }));
        
        // Add dummy training sessions, avoiding duplicates
        dummyTrainingWithType.forEach(session => {
          const exists = allEvents.some(
            event => event.type === 'training' && event.match_id === session.match_id
          );
          
          if (!exists) {
            allEvents.push(session);
          }
        });
      }
    }
    
    return allEvents;
  };

  return (
    <div className="dashboard">
      <Header />

      {/* SuperAdmin Viewing Indicator */}
      {isSuperAdminViewing && (
        <div className="superadmin-viewing-banner">
          <div className="superadmin-viewing-content">
            <span>SUPERADMIN VIEW: Viewing {teamName} as Admin</span>
            <button 
              className="exit-view-button" 
              onClick={exitSuperAdminView}
            >
              <FaArrowLeft /> Exit View
            </button>
          </div>
        </div>
      )}

      {showWelcomeModal && (
        <div className="welcome-modal">
          <div className="welcome-content">
            {isPlayerManager ? (
              <>
                <h2>Hi {firstName},</h2>
                <p className="personal-message">
                  Thanks for joining FirstWhistle — I'm really glad to have you on board.
                </p>
                <p className="personal-message">
                  I built this platform because I was tired of spending hours chasing people to see if they could play, only to end up with too few players or having to cancel games. FirstWhistle saves you that time and hassle.
                </p>
                <p className="personal-message">
                  But it's not just about organisation. The FIFA-style player profiles, achievements, and Golden Boot leaderboard keep players engaged and competitive. It's amazing how much more committed people become when they can track their progress and compete for top scorer.
                </p>
                <p className="personal-message">
                  Thanks for believing in it.
                </p>
                <p className="founder-signature">
                  Dean Hodges<br />
                  <span>Founder, FirstWhistle</span>
                </p>
                <div className="welcome-buttons">
                  <button onClick={handleOpenInviteModal} className="primary-btn">
                    Invite Players
                  </button>
                  <button onClick={handleWelcomeModalDismiss} className="secondary-btn">
                    Start
                  </button>
                </div>
              </>
            ) : isCoach ? (
              <>
                <div className="welcome-logo">
                  <img src={logo} alt="FirstWhistle" className="logo-shield" />
                  {(() => {
                    const teamId = sessionStorage.getItem('selected_team_id');
                    if (teamId) {
                      return (
                        <>
                          <span className="partnership-plus">+</span>
                          <img 
                            src={`https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`}
                            alt="Club Logo" 
                            className="club-logo"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
                <h2>Hi {(() => {
                  const firstName = sessionStorage.getItem('first_name');
                  if (firstName) {
                    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
                  }
                  return 'Coach';
                })()},</h2>
                <p className="personal-message">
                  Thanks for joining FirstWhistle. I'm really glad to have you on board.
                </p>
                <p className="personal-message">
                  I built this platform as a parent and volunteer, seeing how crucial the connection between coaches and parents is for player development. I've seen firsthand how committed coaches like you put in countless hours while juggling admin, communication and training.
                </p>
                <p className="personal-message">
                  FirstWhistle aims to strengthen that parent-coach relationship by making communication seamless. When parents and coaches work together with the player's best interests at heart, that's when real development happens.
                </p>
                <div className="founder-signature">
                  <div className="founder-image">
                    <img 
                      src="https://firstwhistle.app/static/media/about-the-founder.e3953218941e2c167707.jpg" 
                      alt="Dean Hodges" 
                      className="founder-photo"
                    />
                  </div>
                  <div className="founder-text">
                    Dean Hodges<br />
                    <span>Founder, FirstWhistle</span>
                  </div>
                </div>
                <div className="welcome-buttons">
                  <button onClick={handleWelcomeModalDismiss} className="primary-btn">
                    Let's Go
                  </button>
                </div>
              </>
            ) : isParent ? (
              <>
                <div className="welcome-logo">
                  <img src={logo} alt="FirstWhistle" className="logo-shield" />
                  {(() => {
                    const teamId = sessionStorage.getItem('selected_team_id');
                    if (teamId) {
                      return (
                        <>
                          <span className="partnership-plus">+</span>
                          <img 
                            src={`https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`}
                            alt="Club Logo" 
                            className="club-logo"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </>
                      );
                    }
                    return null;
                  })()}
                </div>
                <h2>Hi {(() => {
                  const firstName = sessionStorage.getItem('first_name');
                  if (firstName) {
                    return firstName.charAt(0).toUpperCase() + firstName.slice(1);
                  }
                  return 'there';
                })()},</h2>
                <p className="personal-message">
                  Thanks for joining FirstWhistle. I'm really glad to have you on board.
                </p>
                <p className="personal-message">
                  I created this app as a parent myself, tired of missing important updates buried in group chats and the constant back-and-forth about match availability. I wanted something that kept parents connected to {(() => {
                    const childName = sessionStorage.getItem('child_name');
                    if (childName) {
                      const firstName = childName.split(' ')[0];
                      return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)}'s`;
                    }
                    return "child's";
                  })()} football journey while creating a record of their achievements individually, as well as a team.
                </p>
                <p className="personal-message">
                  FirstWhistle helps you busy people stay in the loop, share and celebrate {(() => {
                    const childName = sessionStorage.getItem('child_name');
                    if (childName) {
                      const firstName = childName.split(' ')[0];
                      return `${firstName.charAt(0).toUpperCase() + firstName.slice(1)}'s`;
                    }
                    return "child's";
                  })()} achievements, and be part of their sporting development.
                </p>
                <div className="founder-signature">
                  <div className="founder-image">
                    <img 
                      src="https://firstwhistle.app/static/media/about-the-founder.e3953218941e2c167707.jpg" 
                      alt="Dean Hodges" 
                      className="founder-photo"
                    />
                  </div>
                  <div className="founder-text">
                    Dean Hodges<br />
                    <span>Founder, FirstWhistle</span>
                  </div>
                </div>
                <div className="welcome-buttons">
                  <button onClick={handleWelcomeModalDismiss} className="primary-btn">
                    Let's Go
                  </button>
                </div>
              </>
            ) : playerID && !isManager ? (
              <>
                <h2>Hi {firstName},</h2>
                <p className="personal-message">
                  Thanks for joining FirstWhistle — I'm really glad to have you on board.
                </p>
                <p className="personal-message">
                  I created this platform to make grassroots football more enjoyable and organized for players like you. No more confusion about match times, locations, or who's playing.
                </p>
                <p className="personal-message">
                  You can easily check your upcoming matches, confirm your availability, and track your progress throughout the season.
                </p>
                <p className="personal-message">
                  Thanks for being part of it.
                </p>
                <p className="founder-signature">
                  Dean Hodges<br />
                  <span>Founder, FirstWhistle</span>
                </p>
                <div className="welcome-buttons">
                  <button onClick={handleWelcomeModalDismiss} className="primary-btn">
                    Let's Go
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Hi there,</h2>
                <p className="personal-message">
                  Thanks for joining FirstWhistle — I'm really glad to have you on board as a team manager.
                </p>
                <p className="personal-message">
                  I set this up as a volunteer manager who was tired of chasing replies in WhatsApp groups and spending more time on admin than actually developing the team. I wanted something better, something that made grassroots football management straightforward while feeling professional.
                </p>
                <p className="personal-message">
                  FirstWhistle is about putting the focus back on football, not paperwork. It's about simplifying the chaos of organising teams, tracking attendance, and managing fixtures so you can concentrate on building a great team.
                </p>
                <p className="personal-message">
                  Thanks for believing in it.
                </p>
                <p className="founder-signature">
                  Dean Hodges<br />
                  <span>Founder, FirstWhistle</span>
                </p>
                <div className="welcome-buttons">
                  <button onClick={handleOpenInviteModal} className="primary-btn">
                    Invite Players
                  </button>
                  <button onClick={handleWelcomeModalDismiss} className="secondary-btn">
                    Start
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      <CalendarCarousel 
        dates={generateDates()}
        selectedDate={selectedDate}
        onDateSelect={setSelectedDate}
        events={getMergedEventsForCalendar()}
        onEventResponse={handleRespond}
        hasTraining={hasTraining}
      />

      <PushPermissionBanner />
    
      {/* 2025 Wrapped Button - Only show for players on friendly teams from 7pm on Monday 15th December 2025 */}
      {(() => {
        const userType = sessionStorage.getItem('user_type');
        const teamType = sessionStorage.getItem('selected_team_type');
        // Check if Season Wrapped should be available (from 7pm on Monday 15th December 2025)
        const releaseDate = new Date('2025-12-15T19:00:00Z'); // 7pm on Monday 15th December 2025 (UTC)
        const now = new Date();
        // TEMPORARILY DISABLED: Allow Wrapped to be visible before release date
        const isAvailable = true; // Temporarily set to true - was: now >= releaseDate;
        
        if (userType === 'player' && teamType === 'friendly' && isAvailable) {
          return (
            <div className="parent-invite-small dashboard" style={{ marginTop: '15px', marginBottom: '15px' }}>
              <div className="container">
                <div className="invite-small-card" style={{ background: 'linear-gradient(135deg, rgba(10, 179, 216, 0.2), rgba(203, 7, 206, 0.2))', cursor: 'pointer' }} onClick={() => {
                  // Show paywall for non-pro users, wrapped for pro users
                  if (!isPro) {
                    setShowSeasonWrappedProModal(true);
                  } else {
                    setShowSeasonWrappedDirect(true);
                  }
                }}>
                  <div className="invite-small-header" style={{ cursor: 'pointer' }}>
                    <div className="invite-header-content">
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FaTrophy style={{ color: '#ffd700' }} />
                        View Your 2025 Wrapped
                      </h3>
                      <p>Relive your best moments from 2025! See your matches, goals, achievements and more.</p>
                      {/* Show "pro feature only" message for all users */}
                      <p style={{ marginTop: '8px', fontSize: '0.85rem', color: '#ffd700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaCrown style={{ fontSize: '0.9rem' }} />
                        This is a pro feature only
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    
      {/* Add a single dummy data notice below the calendar */}
      {showDummyData && (
        <div className="container">
          {/* Add a notice for coaches to add players */}
          {(isCoach || isPlayerManager) && (
            <div className="dummy-data-notice global-notice coach-add-players-notice">
              <h4>Ready to get started?</h4>
              <p>We're showing example data throughout the app to help you experience its features. This will be replaced with your actual data once you start using the app.</p>
              <div className="coach-checklist">
                {(() => {
                  const minimumPlayers = MINIMUM_SQUAD_PLAYERS;
                  const hasPlayers = totalPlayers >= minimumPlayers;
                  const playersStillNeeded = Math.max(0, minimumPlayers - totalPlayers);
                  const isAcademyTeam = sessionStorage.getItem('selected_team_type') === 'academy';
                  const hasAllParentsInvited = !isAcademyTeam || playersWithParents >= totalPlayers;
                  // This checklist only shows in demo. Invite Parents stays open until demo ends.
                  const inviteParentsComplete = false;
                  const hasTrainingSchedule = hasRealTrainingSessions;
                  const hasMatches = allMatches.filter(m => m.type !== 'training').length > 0;
                  
                  // Friendly/squad: gate steps until minimum players (and parents for academy-style rules don't apply)
                  // Academy: gamification only — coaches can create training/matches in any order
                  const gateSteps = !isAcademyTeam;
                  const canProceedToNextSteps = hasPlayers && hasAllParentsInvited;
                  const canAddMatchInWalkthrough = isAcademyTeam || canProceedToNextSteps || hasTrainingSchedule;
                  const canAddTrainingInWalkthrough = isAcademyTeam || canProceedToNextSteps;
                  
                  let itemNumber = 1;
                  
                  return (
                    <ul className="checklist-items">
                      <li className={`checklist-item ${hasPlayers ? 'completed' : ''}`}>
                        {renderChecklistIcon(hasPlayers)}
                        <span className="check-text">
                          <span className="check-main-text">
                            <span className="add-players-title">
                              <span>
                                <span className="step-number">{itemNumber++}.</span> Add Players
                              </span>
                              {!hasPlayers && (
                                <span className="check-action-buttons">
                                  <button 
                                    className="check-action-btn"
                                    onClick={() => handleNavigate('/players')}
                                  >
                                    Add
                                  </button>
                                  {pendingPlayers > 0 && (
                                    <button 
                                      className="check-action-btn check-action-btn--secondary"
                                      onClick={() => handleNavigate('/players?filter=pending')}
                                    >
                                      Approve Pending ({pendingPlayers})
                                    </button>
                                  )}
                                </span>
                              )}
                              {renderChecklistCompleteLabel(hasPlayers)}
                            </span>
                            {!hasPlayers && (
                              <>
                                <div className="progress-bar-container">
                                  <div className="progress-bar">
                                    <div 
                                      className="progress-bar-fill" 
                                      style={{ width: `${Math.min((totalPlayers / minimumPlayers) * 100, 100)}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <span className="progress-text">
                                  {pendingPlayers > 0
                                    ? `${pendingPlayers} ${pendingPlayers === 1 ? 'Player' : 'Players'} Pending. Approve or Add ${playersStillNeeded} ${playersStillNeeded === 1 ? 'player' : 'players'} to complete step 1`
                                    : `Add ${playersStillNeeded} more ${playersStillNeeded === 1 ? 'player' : 'players'} to complete Step 1`}
                                </span>
                              </>
                            )}
                          </span>
                        </span>
                      </li>
                      {isAcademyTeam && (
                        <li className={`checklist-item ${inviteParentsComplete ? 'completed' : ''} ${gateSteps && !hasPlayers ? 'disabled-step' : ''}`}>
                          {renderChecklistIcon(inviteParentsComplete)}
                          <span className="check-text">
                            <span className="check-main-text">
                              <span className="add-players-title">
                                <span>
                                  <span className="step-number">{itemNumber++}.</span> Invite Parents
                                </span>
                                {!inviteParentsComplete && (
                                  <button 
                                    className={`check-action-btn ${gateSteps && !hasPlayers ? 'disabled' : ''}`}
                                    onClick={(!gateSteps || hasPlayers) ? () => handleNavigate('/players') : (e) => e.preventDefault()}
                                  >
                                    Add
                                  </button>
                                )}
                                {renderChecklistCompleteLabel(inviteParentsComplete)}
                              </span>
                              {!inviteParentsComplete && (
                                <>
                                  <div className="progress-bar-container">
                                    <div className="progress-bar">
                                      <div 
                                        className="progress-bar-fill" 
                                        style={{ width: `${totalPlayers > 0 ? Math.min((playersWithParents / totalPlayers) * 100, 100) : 0}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                  <span className="progress-text">
                                    {totalPlayers > 0
                                      ? `${playersWithParents} / ${totalPlayers} players have parents invited`
                                      : 'Add players, then invite a parent for each one'}
                                  </span>
                                </>
                              )}
                            </span>
                          </span>
                        </li>
                      )}
                      {hasTraining && (
                        <li className={`checklist-item ${hasTrainingSchedule ? 'completed' : ''} ${gateSteps && !canProceedToNextSteps ? 'disabled-step' : ''}`}>
                          {renderChecklistIcon(hasTrainingSchedule)}
                          <span className="check-text">
                            <span className="check-main-text">
                              <span className="add-players-title">
                                <span>
                                  <span className="step-number">{itemNumber++}.</span> Add Training Schedule
                                </span>
                                {!hasTrainingSchedule && (
                                  <button 
                                    className={`check-action-btn ${gateSteps && !canProceedToNextSteps ? 'disabled' : ''}`}
                                    onClick={canAddTrainingInWalkthrough ? () => handleNavigate('/createTrainingSession') : (e) => e.preventDefault()}
                                  >
                                    Add
                                  </button>
                                )}
                                {renderChecklistCompleteLabel(hasTrainingSchedule)}
                              </span>
                            </span>
                          </span>
                        </li>
                      )}
                      <li className={`checklist-item ${hasMatches ? 'completed' : ''} ${gateSteps && !canAddMatchInWalkthrough ? 'disabled-step' : ''}`}>
                        {renderChecklistIcon(hasMatches)}
                        <span className="check-text">
                          <span className="check-main-text">
                            <span className="add-players-title">
                              <span>
                                <span className="step-number">{itemNumber++}.</span> Create your first match
                              </span>
                              {!hasMatches && (
                                <button 
                                  className={`check-action-btn ${gateSteps && !canAddMatchInWalkthrough ? 'disabled' : ''}`}
                                  onClick={canAddMatchInWalkthrough ? () => handleNavigate('/createMatch') : (e) => e.preventDefault()}
                                >
                                  Add
                                </button>
                              )}
                              {renderChecklistCompleteLabel(hasMatches)}
                            </span>
                          </span>
                        </span>
                      </li>
                    </ul>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show "Invite Parents" notice separately if checklist is hidden but parents aren't all invited */}
      {!showDummyData && (isCoach || isPlayerManager) && (
        <div className="container">
          {(() => {
            const isAcademyTeam = sessionStorage.getItem('selected_team_type') === 'academy';
            const hasAllParentsInvited = !isAcademyTeam || playersWithParents >= totalPlayers;
            const hasMatches = allMatches.filter(m => m.type !== 'training').length > 0;
            
            // Show this notice if: academy team, has matches (so main checklist is hidden), but parents aren't all invited
            if (isAcademyTeam && hasMatches && !hasAllParentsInvited && totalPlayers > 0) {
              return (
                <div className="dummy-data-notice global-notice coach-add-players-notice">
                  <h4>Invite Parents</h4>
                  <p>Some players don't have parents invited yet. Invite parents to keep families connected.</p>
                  <div className="coach-checklist">
                    <ul className="checklist-items">
                      <li className={`checklist-item ${hasAllParentsInvited ? 'completed' : ''}`}>
                        {renderChecklistIcon(hasAllParentsInvited)}
                        <span className="check-text">
                          <span className="check-main-text">
                            <span className="add-players-title">
                              <span>
                                <span className="step-number">1.</span> Invite Parents
                              </span>
                              {!hasAllParentsInvited && (
                                <button 
                                  className="check-action-btn"
                                  onClick={() => handleNavigate('/players')}
                                >
                                  Add
                                </button>
                              )}
                              {renderChecklistCompleteLabel(hasAllParentsInvited)}
                            </span>
                            {!hasAllParentsInvited && (
                              <>
                                <div className="progress-bar-container">
                                  <div className="progress-bar">
                                    <div 
                                      className="progress-bar-fill" 
                                      style={{ width: `${totalPlayers > 0 ? Math.min((playersWithParents / totalPlayers) * 100, 100) : 0}%` }}
                                    ></div>
                                  </div>
                                </div>
                                <span className="progress-text">{playersWithParents} / {totalPlayers} players have parents invited</span>
                              </>
                            )}
                          </span>
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {/* Setup reminders when walkthrough is hidden but training/match still incomplete */}
      {!showDummyData && (isCoach || isPlayerManager) && (() => {
        const hasTrainingSchedule = hasRealTrainingSessions;
        const hasMatches = allMatches.filter(m => m.type !== 'training').length > 0;
        const showTrainingReminder = hasTraining && !hasTrainingSchedule;
        const showMatchReminder = !hasMatches;

        if (!showTrainingReminder && !showMatchReminder) return null;

        let reminderStep = 1;

        return (
          <div className="container">
            <div className="dummy-data-notice global-notice coach-add-players-notice">
              <h4>Keep going!</h4>
              <p>Finish setting up your team to replace the example data with your own.</p>
              <div className="coach-checklist">
                <ul className="checklist-items">
                  {hasTraining && (
                    <li className={`checklist-item ${hasTrainingSchedule ? 'completed' : ''}`}>
                      {renderChecklistIcon(hasTrainingSchedule)}
                      <span className="check-text">
                        <span className="check-main-text">
                          <span className="add-players-title">
                            <span>
                              <span className="step-number">{reminderStep++}.</span> Add Training Schedule
                            </span>
                            {!hasTrainingSchedule && (
                              <button
                                className="check-action-btn"
                                onClick={() => handleNavigate('/createTrainingSession')}
                              >
                                Add
                              </button>
                            )}
                            {renderChecklistCompleteLabel(hasTrainingSchedule)}
                          </span>
                        </span>
                      </span>
                    </li>
                  )}
                  <li className={`checklist-item ${hasMatches ? 'completed' : ''}`}>
                    {renderChecklistIcon(hasMatches)}
                    <span className="check-text">
                      <span className="check-main-text">
                        <span className="add-players-title">
                          <span>
                            <span className="step-number">{reminderStep++}.</span> Create your first match
                          </span>
                          {!hasMatches && (
                            <button
                              className="check-action-btn"
                              onClick={() => handleNavigate('/createMatch')}
                            >
                              Add
                            </button>
                          )}
                          {renderChecklistCompleteLabel(hasMatches)}
                        </span>
                        {!hasMatches && hasTrainingSchedule && totalPlayers < MINIMUM_SQUAD_PLAYERS && (
                          <span className="progress-text">
                            Your training schedule is set up. We still recommend building your squad to at least {MINIMUM_SQUAD_PLAYERS} players when you can.
                          </span>
                        )}
                      </span>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );
      })()}

      {(isAdminUser || isCoach || isPlayerManager) && (
        <div className="container">
          {/* Count navigation items and add appropriate class */}
          {(() => {
            // Calculate the number of items that will be shown
            let itemCount = 0;
            
            // Count each possible item
            if (true) itemCount++; // Create Match is always visible
            if (hasTraining) itemCount++; // Add Training
            
            // Replace separate buttons with a single "Manage Players/Parents" button
            // Show this button if either condition would have shown one of the original buttons
            if (!isYoungerThanU13() || isCoach) itemCount++; // Manage Players/Parents
            
            if (pendingPlayers > 0) itemCount++; // Pending Players
            
            const navClass = `stats-container dashboard-nav items-${itemCount}`;
            
            const minimumPlayers = MINIMUM_SQUAD_PLAYERS;
            const hasPlayers = totalPlayers >= minimumPlayers;
            const isAcademyTeam = sessionStorage.getItem('selected_team_type') === 'academy';
            const hasAllParentsInvited = !isAcademyTeam || playersWithParents >= totalPlayers;
            const hasMatches = allMatches.filter(m => m.type !== 'training').length > 0;
            const meetsAcademyParentRules = !isAcademyTeam || hasAllParentsInvited || hasMatches;
            // Academy: always allow — nudge via tooltips only. Friendly/squad: keep existing gates.
            const canCreateTraining = isAcademyTeam || (hasPlayers && meetsAcademyParentRules);
            const canCreateMatch = isAcademyTeam || ((hasPlayers || hasRealTrainingSessions) && meetsAcademyParentRules);
            const usingTrainingBypass = canCreateMatch && !hasPlayers && hasRealTrainingSessions;
            
            const getCreateMatchTitle = () => {
              if (isAcademyTeam) {
                if (!hasAllParentsInvited && totalPlayers > 0) {
                  return `Create a match (${playersWithParents} of ${totalPlayers} players have parents invited — we recommend inviting all parents)`;
                }
                if (!hasPlayers) {
                  return `Create a match (we recommend at least ${minimumPlayers} players on your squad)`;
                }
                return 'Create Match';
              }
              if (!canCreateMatch) {
                return `You need at least ${minimumPlayers} players to create a match. You currently have ${totalPlayers} players.`;
              }
              if (usingTrainingBypass) {
                return `Create a match (training is set up; we recommend at least ${minimumPlayers} players on your squad)`;
              }
              return 'Create Match';
            };

            const getCreateTrainingTitle = () => {
              if (isAcademyTeam) {
                if (!hasAllParentsInvited && totalPlayers > 0) {
                  return `Add Training (${playersWithParents} of ${totalPlayers} players have parents invited — we recommend inviting all parents)`;
                }
                if (!hasPlayers) {
                  return `Add Training (we recommend at least ${minimumPlayers} players on your squad)`;
                }
                return 'Add Training';
              }
              if (!canCreateTraining) {
                return `You need at least ${minimumPlayers} players to create a training session. You currently have ${totalPlayers} players.`;
              }
              return 'Add Training';
            };
            
            return (
              <div className={navClass}>
                <div 
                  className={`stats-column ${!canCreateMatch ? 'disabled' : ''}`} 
                  onClick={canCreateMatch ? () => handleNavigate('/createMatch') : (e) => e.preventDefault()}
                  title={getCreateMatchTitle()}
                >
                  <MdSportsSoccer size={36} color="#8c8e8f" />
                  <p>Create Match</p>
                </div>
                {hasTraining && (
                  <div 
                    className={`stats-column ${!canCreateTraining ? 'disabled' : ''}`} 
                    onClick={canCreateTraining ? () => handleNavigate('/createTrainingSession') : (e) => e.preventDefault()}
                    title={getCreateTrainingTitle()}
                  >
                    <FaRunning size={36} color="#8c8e8f" />
                    <p>Add Training</p>
                  </div>
                )}
                
                {/* Replace the two separate buttons with a single "Manage Players/Parents" button */}
                {(!isYoungerThanU13() || isCoach) && (
                  <div className="stats-column" onClick={() => handleNavigate('/players')}>
                    <FaUsers size={36} color="#8c8e8f" />
                    <p>
                      {sessionStorage.getItem('selected_team_type') === 'academy' 
                        ? 'Manage Players/Parents' 
                        : 'Manage Players'}
                    </p>
                  </div>
                )}
                
                {pendingPlayers > 0 && (
                  <div className="stats-column" onClick={() => handleNavigate('/players?filter=pending')}>
                     <FaUsers size={36} color="#8c8e8f" />
                    <div className="pending-indicator">
                     
                      <span className="pending-count">{pendingPlayers}</span>
                    </div>
                    <p>Pending Players</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Invite Player</h2>
            <p>Copy the code below and share it with the player you want to invite:</p>
            <div className="invite-code">
              <p><strong>Player Code</strong>:&nbsp;<span>{inviteCode}</span></p>
              <button onClick={copyToClipboard}>Copy Invite Link</button>
            </div>
            
            <div className="divider">
              <span>or send directly via SMS</span>
            </div>
            
            <div className="sms-invite">
              <div className="form-group">
                <label>Mobile Number (optional)</label>
                <input 
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="07xxx xxxxx"
                  disabled={isSendingSMS || smsSent}
                />
                {phoneError && <p className="error-message">{phoneError}</p>}
              </div>
              
              {smsSent ? (
                <p className="success-message">SMS invitation sent successfully!</p>
              ) : (
                <button 
                  onClick={sendSMSInvite} 
                  disabled={isSendingSMS || !phoneNumber}
                  className={isSendingSMS ? 'loading' : ''}
                >
                  {isSendingSMS ? 'Sending...' : 'Send SMS Invite'}
                </button>
              )}
              
              {process.env.NODE_ENV === 'development' && (
                <p className="info-text">
                  Note: During testing, all SMS messages will be sent to the test number.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent Invite Section - Only shown for parent users with permission to respond to matches */}
      {isParent && authService.canRespondToMatches(sessionStorage.getItem('player_id')) && (
        <div className="parent-invite-small dashboard">
          {/* Invite Small Card - Available to all parents */}
            <div className="container">
              <div className="invite-small-card">
                <div className="invite-small-header" onClick={() => setInviteExpanded(!inviteExpanded)}>
                  <div className="invite-header-content">
                    <h3>Invite Extended Family</h3>
                    <p>Invite grandparents, aunts, uncles, and other family members to follow {childName?.split(' ')[0] || 'your child'}'s football journey.</p>
                  </div>
                  <div className={`invite-toggle ${inviteExpanded ? 'expanded' : ''}`}>
                    <span>+</span>
                  </div>
                </div>
                
                {inviteExpanded && (
                  <div className="invite-small-content">
                    <div className="invite-notice">
                      <p><strong>Note:</strong> This is for inviting family members to follow your child's journey. 
                      To give your child access to their own account, go to <strong className="clickable-text" onClick={() => navigate('/parent-settings')}>Main Menu → Child Access Settings</strong>.</p>
                    </div>
                    
                    <div className="invite-options">
                      {/* Temporarily hidden - Share Registration Link section
                      <div className="invite-option">
                        <h4>Share Registration Link</h4>
                        <p>Copy the link and send in a message</p>
                        <div className="invite-small-link">
                          <input 
                            type="text" 
                            value={`${process.env.REACT_APP_PUBLIC_URL.replace(/\/+$/, '')}/register-parent?t=${sessionStorage.getItem('selected_team_id')}&p=${sessionStorage.getItem('player_id')}`}
                            readOnly 
                            className="invite-small-input"
                          />
                          <button 
                            onClick={() => {
                              const inviteLink = `${process.env.REACT_APP_PUBLIC_URL.replace(/\/+$/, '')}/register-parent?t=${sessionStorage.getItem('selected_team_id')}&p=${sessionStorage.getItem('player_id')}`;
                              navigator.clipboard.writeText(inviteLink).then(() => {
                                showSuccess('Registration link copied!');
                              }).catch(() => {
                                showError('Failed to copy link');
                              });
                            }}
                            className="invite-small-copy"
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                      */}
                      
                      <div className="invite-option">
                        <h4>Send Text Invitation</h4>
                        <p>Enter details to send an invite directly</p>
                        <div className="sms-invite-form">
                          <div className="name-fields">
                            <div className="name-field-wrapper">
                              <input 
                                type="text" 
                                placeholder="First Name"
                                value={inviteFirstName}
                                onChange={(e) => setInviteFirstName(e.target.value)}
                                onBlur={validateChildInvite}
                                className="sms-input first-name"
                              />
                            </div>
                            <div className="name-field-wrapper">
                              <input 
                                type="text" 
                                placeholder="Last Name"
                                value={inviteLastName}
                                onChange={(e) => setInviteLastName(e.target.value)}
                                onBlur={validateChildInvite}
                                className="sms-input last-name"
                              />
                            </div>
                          </div>
                          <select
                            value={inviteRelationship}
                            onChange={(e) => setInviteRelationship(e.target.value)}
                            className="sms-input"
                          >
                            <option value="">Select relationship to child</option>
                            <option value="Mother">Mother</option>
                            <option value="Father">Father</option>
                            <option value="Step-mother">Step-mother</option>
                            <option value="Step-father">Step-father</option>
                            <option value="Guardian">Guardian</option>
                            <option value="Grandmother">Grandmother</option>
                            <option value="Grandfather">Grandfather</option>
                            <option value="Aunt">Aunt</option>
                            <option value="Uncle">Uncle</option>
                            <option value="Other">Other</option>
                          </select>
                          <input 
                            type="tel" 
                            placeholder="Mobile number (07xxx xxxxxx)"
                            value={smsPhoneNumber}
                            onChange={(e) => setSmsPhoneNumber(e.target.value)}
                            className="sms-input"
                          />
                          <div className="checkbox-group">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={canRespondToMatches}
                                onChange={(e) => setCanRespondToMatches(e.target.checked)}
                              />
                              <span className="checkbox-text">Allow this person to respond to match/training requests</span>
                            </label>
                            <p className="checkbox-help">
                              When enabled, this person will receive notifications and can respond to match and training invitations on behalf of {childName?.split(' ')[0] || 'your child'}.
                            </p>
                          </div>
                          <button 
                            onClick={handleSendSMSInvite}
                            disabled={!inviteFirstName.trim() || !inviteLastName.trim() || !inviteRelationship.trim() || !smsPhoneNumber.trim() || isSendingSMS}
                            className="sms-send-btn"
                          >
                            {isSendingSMS ? 'Sending...' : 'Send'}
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="invite-benefits">
                      <h4>What they'll see:</h4>
                      <ul>
                        <li>Match results and highlights</li>
                        <li>Training progress updates</li>
                        <li>Achievements and milestones</li>
                        <li>Team news and announcements</li>
                        {canRespondToMatches && (
                          <>
                            <li>Match and training invitations with ability to respond</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          
          {/* New Year Discount Banner - Shown for NON-PRO parent users only (toggle SHOW_NEW_YEAR_PARENT_OFFER) */}
          {SHOW_NEW_YEAR_PARENT_OFFER && !isPro && (
            <div className="container">
              <div className="new-year-discount-banner">
                <div className="banner-content">
                  <h3>New Year 25% Discount</h3>
                  <p>To celebrate the release of our new app we are offering an exclusive 25% discount on First Whistle Pro.</p>
                  <button 
                    className="banner-cta-button"
                    onClick={() => setShowProModal(true)}
                  >
                    Get Pro
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      <div className="matches-container dashboard">
        <div className="matches-container-header container">
          <h2 className="section-title">
            Fixtures
            {showDummyData && <span className="example-label">Example</span>}
          </h2>
        </div>
        <div className="matches-scroll-container container">
          <div className="matches-scroll-wrapper">
            {(() => {
      
              return showDummyData ? (
                getDummyFixture()
                  .filter(match => {
                    // Filter out dummy matches that have already started
                    const matchDateTime = new Date(`${match.match_date}T${match.match_time}`);
                    const now = new Date();
                    return matchDateTime > now;
                  })
                  .map((match, index) => {
                  return (
                    <div key={match.match_id} className="match-card-wrapper">
                      <MatchCard
                        match={{
                          ...match,
                          id: match.match_id,
                          date: match.match_date,
                          time: match.match_time,
                          location: match.location,
                          response: match.response,
                          max_players: match.max_players || 12,
                          yes: match.yes || [],
                          no: match.no || [],
                          match_date: match.match_date,
                          entry_open: match.entry_open,
                                                      response_updated_at: match.response_updated,
                            responder_first_name: match.responder_first_name,
                            responder_last_name: match.responder_last_name,
                            // For dummy cards, provide a mock onRespond that doesn't call API
                          onRespond: (response, isWaitlist) => {
                            console.log('Example card response:', response, isWaitlist);
                            return false; // Prevent default action
                          }
                        }}
                        onMatchDeleted={null}
                        disableActions={true}
                        disableNavigation={false}
                        onClick={() => navigate('/match/1')}
                      />
                    </div>
                  );
                })
              ) : futureMatches.length > 0 ? (
                futureMatches
                  .filter(match => match.type === 'match')
                  .map((match, index) => {
                    return (
                      <div key={match.match_id} className="match-card-wrapper pro-user">

                        <MatchCard
                          match={{
                            ...match,
                            id: match.match_id,
                            date: match.match_date,
                            time: match.match_time,
                            location: match.location,
                            response: match.response,
                            max_players: match.max_players || 12,
                            yes: match.yes || [],
                            no: match.no || [],
                            match_date: match.match_date,
                            entry_open: match.entry_open,
                            response_updated_at: match.response_updated,
                            responder_first_name: match.responder_first_name,
                            responder_last_name: match.responder_last_name,
                            onRespond: (response, isWaitlist) => handleRespond(match.match_id, response, isWaitlist),
                            // Check if match is finished to hide response totals
                            isFinishedMatch: (() => {
                              if (match.score_white !== null && match.score_colour !== null) {
                                return true; // Has scores, so match is finished
                              }
                              if (match.match_date && match.match_time) {
                                const matchDateTime = new Date(`${match.match_date}T${match.match_time}`);
                                const now = new Date();
                                const duration = parseInt(match.duration) || 90;
                                const matchEndTime = new Date(matchDateTime.getTime() + (duration * 60 * 1000));
                                return now > matchEndTime;
                              }
                              return false;
                            })()
                          }}
                          onMatchDeleted={handleMatchDeleted}
                          disableActions={match.isDummy}
                          disableNavigation={match.isDummy}
                        />
                      </div>
                    );
                  })
              ) : (
                <p style={{color: '#585858', marginTop: '0'}}>No upcoming matches scheduled</p>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* Training Sessions Section - show if team has training enabled OR if user is a parent */}
      {(hasTraining || isParent) && (
        <div className="matches-container dashboard">
          <div className="matches-container-header container">
            <h2 className="section-title">
              Next Training Session
              {showDummyData && <span className="example-label">Example</span>}
            </h2>
          </div>
          <div className="container">
            {(() => {
              
              // First check if showing dummy data
              if (showDummyData === true) {
                // Show dummy training session (existing code)
                const dummyTraining = getDummyTraining()[0];
                return (
                  <div 
                    key={dummyTraining.match_id} 
                    className="match-card-wrapper"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/training/1')}
                  >
                    <div className="match-card future-game training-card">
                      <div className="match-location">
                        Training
                        <div className="match-type">{dummyTraining.location}</div>
                      </div>
                      
                      <div className="match-date">
                        {new Date(dummyTraining.match_date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </div>
                      
                      <div className="match-time">
                          <FaClock /> {dummyTraining.match_time.substring(0, 5)} - {
                          (() => {
                            // Calculate end time
                            const startTimeParts = dummyTraining.match_time.substring(0, 5).split(':');
                            const startHour = parseInt(startTimeParts[0]);
                            const startMinute = parseInt(startTimeParts[1]);
                            const durationMins = parseInt(dummyTraining.duration) || 90;
                            const totalEndMinutes = startHour * 60 + startMinute + durationMins;
                            const endHour = Math.floor(totalEndMinutes / 60);
                            const endMinute = totalEndMinutes % 60;
                            return `${endHour}:${endMinute.toString().padStart(2, '0')}`;
                          })()
                        }
                      </div>
                      
                      <div className="response-totals">
                        <div className="total yes">
                          <div className="count">
                            <span className="current">{dummyTraining.total_yes}</span>
                          </div>
                          <div className="label">Yes</div>
                        </div>
                        
                        <div className="total no">
                          <div className="count">
                            <span className="current">{dummyTraining.total_no}</span>
                          </div>
                          <div className="label">No</div>
                        </div>
                        
                        <div className="total awaiting">
                          <div className="count">
                            <span className="current">{dummyTraining.total_awaiting}</span>
                          </div>
                          <div className="label">Awaiting</div>
                        </div>
                      </div>
                      
                      {/* Only show response buttons if user is not a coach or child */}
                      {!isCoach && (
                        <div className="response-section">
                          <div className="response-buttons">
                            <button 
                              className={`response-btn yes ${dummyTraining.response === 'Yes' ? 'active' : ''} ${authService.isChildBlockedFromResponding() ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!authService.isChildBlockedFromResponding()) {
                                  console.log('Example training - Yes clicked');
                                }
                              }}
                              disabled={authService.isChildBlockedFromResponding()}
                            >
                              Yes
                            </button>
                            <button 
                              className={`response-btn no ${dummyTraining.response === 'No' ? 'active' : ''} ${authService.isChildBlockedFromResponding() ? 'disabled' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!authService.isChildBlockedFromResponding()) {
                                  console.log('Example training - No clicked');
                                }
                              }}
                              disabled={authService.isChildBlockedFromResponding()}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                // Try to get the sessions either from state or from sessionStorage
                const sessions = trainingSessions.length > 0 ? 
                  trainingSessions : 
                  getCachedTrainingSessions();
                
                // Check if we have sessions to display
                if (sessions && sessions.length > 0) {
                  // Display the first training session
                  const nextTraining = sessions[0];
                  return (
                    <div 
                      key={nextTraining.match_id} 
                      className="match-card-wrapper"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/training/${nextTraining.match_id}`)}
                    >
                      <div className={`match-card future-game training-card ${nextTraining.status} ${nextTraining.status && nextTraining.status !== 'active' ? 'has-status-overlay' : ''}`}>
                        {(nextTraining.status === 'cancelled' || nextTraining.status === 'postponed') && (
                          <div className={`status-stamp ${nextTraining.status}`}>
                            {nextTraining.status === 'cancelled' ? 'Cancelled' : 
                             nextTraining.status === 'postponed' ? 'Postponed' : ''}
                          </div>
                        )}
                        
                        <div className="match-location">
                          Training
                          <div className="match-type">{nextTraining.location}</div>
                        </div>
                        
                        <div className="match-date">
                          {new Date(nextTraining.match_date).toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        
                        <div className="match-time">
                          <FaClock /> {nextTraining.match_time.substring(0, 5)} - {
                            (() => {
                              // Calculate end time
                              const startTimeParts = nextTraining.match_time.substring(0, 5).split(':');
                              const startHour = parseInt(startTimeParts[0]);
                              const startMinute = parseInt(startTimeParts[1]);
                              const durationMins = parseInt(nextTraining.duration) || 90;
                              const totalEndMinutes = startHour * 60 + startMinute + durationMins;
                              const endHour = Math.floor(totalEndMinutes / 60);
                              const endMinute = totalEndMinutes % 60;
                              return `${endHour}:${endMinute.toString().padStart(2, '0')}`;
                            })()
                          }
                        </div>
                        
                        <div className="response-totals">
                          <div className="total yes">
                            <div className="count">
                              <span className="current">{nextTraining.total_yes || 0}</span>
                            </div>
                            <div className="label">Yes</div>
                          </div>
                          
                          <div className="total no">
                            <div className="count">
                              <span className="current">{nextTraining.total_no || 0}</span>
                            </div>
                            <div className="label">No</div>
                          </div>
                          
                          <div className="total awaiting">
                            <div className="count">
                              <span className="current">{nextTraining.total_awaiting || 0}</span>
                            </div>
                            <div className="label">Awaiting</div>
                          </div>
                        </div>
                        
                        {/* Show response buttons for all users except coaches, but disable for parents without permission */}
                        {!isCoach && (
                          <div className="response-section">
                            <p>{isParent ? `Can ${sessionStorage.getItem('child_name')?.split(' ')[0] || 'your child'} attend training?` : 'Can you attend training?'}</p>
                            <div className="response-buttons">
                              <button 
                                className={`response-btn yes ${nextTraining.response === 'Yes' ? 'active' : nextTraining.response === 'No' ? 'inactive' : ''} ${authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(sessionStorage.getItem('player_id'))) ? 'disabled' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!authService.isChildBlockedFromResponding() && (!authService.isParent() || authService.canRespondToMatches(sessionStorage.getItem('player_id')))) {
                                    handleRespond(nextTraining.match_id, 'yes', false);
                                  }
                                }}
                                disabled={authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(sessionStorage.getItem('player_id')))}
                              >
                                Yes
                              </button>
                              <button 
                                className={`response-btn no ${nextTraining.response === 'No' ? 'active' : nextTraining.response === 'Yes' ? 'inactive' : ''} ${authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(sessionStorage.getItem('player_id'))) ? 'disabled' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!authService.isChildBlockedFromResponding() && (!authService.isParent() || authService.canRespondToMatches(sessionStorage.getItem('player_id')))) {
                                    handleRespond(nextTraining.match_id, 'no', false);
                                  }
                                }}
                                disabled={authService.isChildBlockedFromResponding() || (authService.isParent() && !authService.canRespondToMatches(sessionStorage.getItem('player_id')))}
                              >
                                No
                              </button>
                            </div>
                            
                            {/* Show message for parents without permission */}
                            {authService.isParent() && !authService.canRespondToMatches(sessionStorage.getItem('player_id')) && (
                              <p className="permission-notice">
                                You need coach approval to respond to training sessions for this child.
                              </p>
                            )}
                            {authService.isChildBlockedFromResponding() && (
                              <p className="permission-notice">
                                Ask your parent to allow training responses in Child Access Settings.
                              </p>
                            )}
                            
                            {/* Show responder information */}
                            <div className="responder-info">
                              <small style={{ fontStyle: 'italic', color: '#666' }}>
                                {nextTraining.response && nextTraining.responder_first_name && nextTraining.responder_last_name ? (
                                  <>
                                    Responded by {nextTraining.responder_first_name.charAt(0).toUpperCase() + nextTraining.responder_first_name.slice(1)} {nextTraining.responder_last_name.charAt(0).toUpperCase() + nextTraining.responder_last_name.slice(1)}
                                    {nextTraining.response_updated ? (
                                      <> on {new Date(nextTraining.response_updated).toLocaleDateString()} at {new Date(nextTraining.response_updated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                    ) : (
                                      <> on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</>
                                    )}
                                  </>
                                ) : (
                                  <>Awaiting response</>
                                )}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } else if (trainingDataFetchedRef.current) {
                  // We've fetched data but there are no sessions
                  return <p style={{color: '#585858', marginTop: '0'}}>No upcoming training sessions scheduled</p>;
                } else {
                  // Data is still being fetched
                  return <p style={{color: '#585858', marginTop: '0'}}>Loading training sessions...</p>;
                }
              }
            })()}
          </div>
        </div>
      )}

      {/* Show Awards for all users except regular coaches */}
      {(() => {
        // Show Awards for all user types except coaches who are not also players or admins
        const shouldShowAwards = !isCoach || isPlayerManager || isParent || isAdminUser;
        
        if (shouldShowAwards) {
          // For parent users, get the child's name from sessionStorage
          // For coaches, get the player name for the selected player
          let achievementsTitle = "Achievements";
          
          if (isParent) {
            const childName = sessionStorage.getItem('child_name');
            if (childName) {
              // Extract first name if the child_name contains multiple names
              const firstName = childName.split(' ')[0];
              achievementsTitle = `${firstName}'s Achievements`;
            }
          } else if (isCoach && firstName && firstName !== 'Player') {
            // For coaches viewing a specific player's achievements
            achievementsTitle = `${firstName}'s Achievements`;
          }
          
          return (
            <div className="awards-container dashboard">
              <div className="awards-header container">
                <h2 className="section-title">
                  {achievementsTitle}
                  {showDummyData && <span className="example-label">Example</span>}
                </h2>
              </div>
              <div className="matches-scroll-container container">
                <div className="matches-scroll-wrapper">
                  <Awards 
                    defaultView="scroll" 
                    showDummy={showDummyData} 
                    showHeader={false}
                    seasonId={currentSeasonId}
                    seasonLoading={!seasonLoaded}
                  />
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Parent Articles Section - Only shown for parent users */}
      {isParent && <ParentArticles />}

      {/* Recent Matches Section */}
      {!isParent && (
        <div className="matches-container dashboard">
          <div className="matches-container-header container">
            <h2 className="section-title">
              Recent Matches
              {showDummyData && <span className="example-label">Example</span>}
            </h2>
          </div>
          {!isPro && !isAdminUser && !isCoach && subscriptionStatus?.trial_expired && recentMatches.length > 1 && (
            <div className="container">
              <div className="pro-upgrade-notice" onClick={() => setShowProModal(true)}>
                <FaCrown className="crown-icon" />
                <span>Upgrade to Pro to View All Match History</span>
              </div>
            </div>
          )}
          <div className="matches-scroll-container container">
            <div className="matches-scroll-wrapper">
              {(() => {
                if (showDummyData) {
                  // Show dummy recent matches when showDummyData is true
                  const dummyMatch = getDummyRecentMatch();
                  return (
                    <div 
                      key={dummyMatch.match_id} 
                      className="match-card-wrapper"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/match/1')}
                    >
                      <MatchCard
                        match={{
                          ...dummyMatch,
                          id: dummyMatch.match_id,
                          match_date: dummyMatch.date,
                          played_match: true,
                          score_white: dummyMatch.score_white,
                          score_colour: dummyMatch.score_colour,
                          location: dummyMatch.location,
                          motm: {
                            first_name: dummyMatch.motm_first_name,
                            last_name: dummyMatch.motm_last_name
                          },
                          goals_scored: dummyMatch.goalScored,
                          opposition: dummyMatch.opposition,
                          response_updated_at: dummyMatch.response_updated,
                          responder_first_name: dummyMatch.responder_first_name,
                          responder_last_name: dummyMatch.responder_last_name
                        }}
                        disableNavigation={false}
                      />
                    </div>
                  );
                } else if (recentMatches.length > 0) {
                  return recentMatches.map((match, index) => (
                    <div 
                      key={match.match_id} 
                      className={`match-card-wrapper ${isPro || isAdminUser || isCoach || !subscriptionStatus?.trial_expired || index === 0 ? 'pro-user' : 'pro-locked'}`}
                    >
                      {!isPro && !isAdminUser && !isCoach && subscriptionStatus?.trial_expired && index > 0 && (
                        <div 
                          className="crown-wrapper" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowProModal(true);
                          }}
                        >
                          <FaCrown className="crown-icon" />
                        </div>
                      )}
                      <div 
                        onClick={(e) => {
                          if (match.isDummy) {
                            e.preventDefault();
                            return;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          if (isPro || isAdminUser || isCoach || !subscriptionStatus?.trial_expired || index === 0) {
                            navigate(`/match/${match.match_id}`);
                          } else {
                            setShowProModal(true);
                          }
                        }}
                        style={{ cursor: match.isDummy ? 'default' : 'pointer' }}
                      >
                        <MatchCard
                          match={{
                            ...match,
                            id: match.match_id,
                            match_date: match.date,
                            played_match: true,
                            score_white: match.score_white,
                            score_colour: match.score_colour,
                            location: match.location,
                            motm: {
                              first_name: match.motm_first_name,
                              last_name: match.motm_last_name
                            },
                            goals_scored: match.goalScored,
                            opposition: match.opposition,
                            response_updated_at: match.response_updated,
                            responder_first_name: match.responder_first_name,
                            responder_last_name: match.responder_last_name
                          }}
                          disableNavigation={match.isDummy || (!isPro && !isAdminUser && !isCoach && subscriptionStatus?.trial_expired && index > 0)}
                        />
                      </div>
                    </div>
                  ));
                } else {
                  return (
                    <div className='container'>
                      <div className="no-matches" style={{color: '#585858'}}>No recent matches found</div>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Match Reports Section - Show only for academy teams */}
      {sessionStorage.getItem('selected_team_type') === 'academy' && (
        <MatchReports showDummyData={showDummyData} />
      )}
        

      {!isParent && (
        <div className="container">
          <h3 className="section-title">
            Your Last 5 Results
            {showDummyData && <span className="example-label">Example</span>}
          </h3>
          <div className="last-results">
              {(() => {
        
                if (showDummyData) {
                  // Show dummy last results data when showDummyData is true
                  const dummyResults = [getDummyLastResult()];
                  
                  return (
                    <ul className="results-list">
                      {dummyResults.map((match, index) => (
                        <li 
                          key={match.match_id} 
                          className="result-item"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate('/match/1')}
                        >
                          <div className="result-info">
                            <span className="date">
                              {new Date(match.match_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'numeric',
                                year: '2-digit'
                              })}
                            </span>
                            <span className={`team-a ${match.player_team.toLowerCase() === 'white' ? 'player-team' : ''}`}>
                              {match.team_a || 'Whites'}
                            </span>
                            <span className="score">
                              {match.white_score} - {match.colour_score}
                            </span>
                            <span className={`team-b ${match.player_team.toLowerCase() === 'colour' ? 'player-team' : ''}`}>
                              {match.team_b || 'Colours'}
                            </span>
                            <span className="goals">
                              {parseInt(match.goals_scored) > 0 ? `⚽ ${match.goals_scored}` : '-'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  );
                } else if (lastResults.length > 0) {
                  return (
                    <ul className="results-list">
                      {lastResults.map((match, index) => (
                        <li 
                          key={match.match_id} 
                          className={`result-item ${!isPro && !isAdminUser && !isCoach && subscriptionStatus?.trial_expired && index > 0 ? 'blurred' : ''}`}
                          onClick={(e) => {
                            if (match.isDummy) {
                              e.preventDefault();
                              return;
                            }
                            e.preventDefault();
                            e.stopPropagation();
                            if (isPro || isAdminUser || isCoach || !subscriptionStatus?.trial_expired || index === 0) {
                              navigate(`/match/${match.match_id}`);
                            } else {
                              setShowProModal(true);
                            }
                          }}
                          style={{ cursor: match.isDummy ? 'default' : 'pointer' }}
                        >
                          {!isPro && !isAdminUser && !isCoach && subscriptionStatus?.trial_expired && index > 0 && (
                            <div 
                              className="crown-wrapper" 
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowProModal(true);
                              }}
                            >
                              <FaCrown className="crown-icon" />
                            </div>
                          )}
                          <div className="result-info">
                            <span className="date">
                              {new Date(match.match_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'numeric',
                                year: '2-digit'
                              })}
                            </span>
                            <span className={`team-a ${match.team_a === sessionStorage.getItem('selected_team_name') ? 'player-team' : ''}`}>
                              {match.team_a}
                            </span>
                            <span className="score">
                              {match.white_score} - {match.colour_score}
                            </span>
                            <span className={`team-b ${match.team_b === sessionStorage.getItem('selected_team_name') ? 'player-team' : ''}`}>
                              {match.team_b}
                            </span>
                            <span className="goals">
                              {parseInt(match.goals_scored) > 0 ? `⚽ ${match.goals_scored}` : '-'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  );
                } else {
                  return <p className="no-results">No recent matches found</p>;
                }
              })()}
          </div>
          
          {matches && matches.length > 0 && (
            <div className="matches match-results">
              <h3>Your Last 5 Matches</h3>
              <div className="match-grid">
                {matches.map((match, index) => (
                  <div key={index} className="match-card">
                    {match.result === 'win' ? 'W' : match.result === 'loss' ? 'L' : 'D'}
                  </div>
                ))}
              </div>
            </div>
          )}

          {showAchievement && currentAchievement && (
            <AchievementModal 
              achievement={currentAchievement}
              onClose={handleAchievementClose}
            />
          )}
        </div>
      )}

      <ProFeatureModal 
        isOpen={showProModal} 
        onClose={() => setShowProModal(false)}
        featureName="Match History"
      />

      {/* 2025 Wrapped Pro Modal - Shows paywall for non-pro users */}
      <ProFeatureModal 
        isOpen={showSeasonWrappedProModal} 
        onClose={() => setShowSeasonWrappedProModal(false)}
        featureName="Season Wrapped"
      />

      {/* 2025 Wrapped Modal */}
      <SeasonWrappedModal
        isOpen={showSeasonWrappedModal}
        onClose={() => {
          setShowSeasonWrappedModal(false);
          // Mark as seen so it doesn't show again automatically
          localStorage.setItem('has_seen_2025_wrapped', 'true');
        }}
      />

      {/* 2025 Wrapped - Direct view when button is clicked */}
      {showSeasonWrappedDirect && (
        <SeasonWrapped
          isOpen={showSeasonWrappedDirect}
          onClose={() => {
            setShowSeasonWrappedDirect(false);
          }}
        />
      )}
    </div>
  );
}

export default Dashboard; 